import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Linking,
  Image,
  Modal,
  Dimensions,
  Animated,
  I18nManager,
  PanResponder,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import { MediaUploadError, uploadMedia } from '../media/mediaUpload';
import { readFileSizeBytes } from '../media/fileSize';
import { requestForegroundLocationPermission, readLivePoint } from '../media/captureLocation';
import { startLiveLocationLoop } from '../media/liveLocationLoop';
import { requestBackgroundLocationPermission, startBackgroundLocation, stopBackgroundLocation } from '../media/backgroundLocation';
import { forgetLiveShare, rememberLiveShare } from '../media/liveLocationTask';
import { voiceNoteCost } from '../voice/policy';
import MessageVideo from '../components/MessageVideo';
import MessageVoice from '../components/MessageVoice';
import MessageVoiceOrder, { formatOrderLength, parseVoiceOrderCard } from '../components/MessageVoiceOrder';
import MessageLocation, { parseLocationCard } from '../components/MessageLocation';
import LiveLocationBar from '../components/LiveLocationBar';
import MessageLiveLocation, { parseLiveShareCard } from '../components/MessageLiveLocation';
import LiveLocationPanel from '../components/LiveLocationPanel';
import VoiceRecorderSheet from '../components/VoiceRecorderSheet';
import { requestMicrophonePermission, voiceRecordingAvailable, type VoiceRecording } from '../voice/recorder';
import Ionicons from '@expo/vector-icons/Ionicons';
import { spacing, fontSize, formControlMetrics, ThemeColors, radius } from '../theme';
import { useTheme, useThemeMode } from '../theme/ThemeContext';
import { api, BASE_URL, mediaUrl, NetworkError, ApiError, Message, MessagePriceQuote, CallPriceQuote, LocationShare } from '../services/api';
import { getMessages as e2eeGetMessages } from '../e2ee/messages';
import { conversationEncryption, encryptIfPossible, type EncryptionState } from '../e2ee/session';
import { LabelWithInfo } from '../components/InfoHint';
import { conversationMood } from '../avatarMood/plutchik';
import { moodTint } from '../avatarMood/tint';
import { rememberOutgoing } from '../e2ee/plaintextCache';
import { mergeFetched, upsertLocal } from './messageList';
import { formatTime, timeAgo } from '../utils/time';
import { formatMoney, formatPerMinute } from '../data/currencies';
import { applyReaction, isTogglingOff } from '../utils/reactions';
import { splitMessageLinks, linkHost, firstPreviewableLink, withoutLink } from '../utils/links';
import { cleanLinkPreview, decodeMessagePayload, encodeMessagePayload, type LinkPreview, type LinkView } from '../linkPreview/payload';
import { buildLinkPreview } from '../linkPreview/fetchPreview';
import { LinkCard, LinkPill } from '../components/LinkPreviewViews';
import { isEmojiOnlyMessage } from '../utils/emojiMessage';
import { useTranslation } from '../i18n/I18nContext';
import { formatDisplayCount } from '../i18n/formatCount';
import { parseDurationInput, sanitizeDurationInput } from '../i18n/durationInput';
import { callsEnabled, videoCallsSupported } from '../calls/config';
import { mediaAvailable } from '../calls/media';
import Avatar from '../components/Avatar';
import MeetupDateTimeField from '../components/MeetupDateTimeField';
import { callDuration, callLabel, isMissedCallForViewer } from '../calls/label';
import { CalendarIcon } from '../components/FloatingDock';
import HeaderBackButton from '../components/HeaderBackButton';
import ScreenHeader from '../components/ScreenHeader';
import { loadContactTranslationPreference } from '../services/contactPreferences';
import { storage } from '../services/storage';
import { confirmCalendarConflict } from '../calendar/conflictPrompt';
import { coordinatesFromExif } from '../media/photoLocation';
import { coordinatesAtCapture } from '../media/captureLocation';
import { getDeviceTimeZone } from '../utils/locale';
import { formatNosubjectCalendarEventWhen } from '../calendar/eventPresentation';
import GlassControlButton from '../components/GlassControlButton';
import NotificationOfferCard from '../components/NotificationOfferCard';
import VerifiedBadge from '../components/VerifiedBadge';

// Çağrı ve fiyat satırları mesaj DEĞİL: ikisi de sunucuda okuma anında ayrı
// tablolardan türetiliyor (9.4.10 / D-285), bu yüzden id'leri `messages`
// id'leriyle pekâlâ çakışabilir. Alıntı ve "kaynağa git" yolları yalnız gerçek
// mesajlara bakmalı — yoksa yanlış satır alıntılanır.
const isRealMessage = (m: Message) => m.type !== 'call' && m.type !== 'price';

export type MessageInitialAction =
  | 'meetup'
  | 'callReservation'
  | { type: 'callNow'; reservationId: number }
  | null;

type Props = {
  token: string;
  myId: number;
  otherId: number;
  otherName: string;
  otherPhoto: string | null;
  onBack: () => void;
  onStartCall?: (
    reservationId?: number,
    /** 3.10.5 — kullanıcıya gösterilen dakika ücreti; yalnız anlık aramada geçerli */
    priceStamp?: { amount: number; currency: string } | null,
    /** 9.4.14 — görüntülü mü. Damga da bu seçime göre okunur: görüntülü akışta
     *  görüntülü ücret damgalanır, sesli akışta sesli ücret. */
    video?: boolean
  ) => void;
  onViewProfile: () => void;
  /** Tam ekran çağrı üstteyken görünmeyen sohbet ağ işi üretmez. */
  paused?: boolean;
  initialAction?: MessageInitialAction;
  onInitialActionHandled?: () => void;
};

const MSG_IMG_WIDTH = 220;
const MSG_IMG_MAX_HEIGHT = 330;
const TEXT_BUBBLE_META_WIDTH = 72;
const TEXT_BUBBLE_WIDTH_GUARD = 2;
// 9.32 / D-655 — yazmayı bıraktıktan bu kadar sonra önizleme hazırlanır; yarım
// yazılmış bir adres için sayfa açılmasın.
const LINK_PREVIEW_DELAY_MS = 700;
const SCREEN_WIDTH = Dimensions.get('window').width;
const SCREEN_HEIGHT = Dimensions.get('window').height;

const REACTION_EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🙏'];
type PhotoSource = 'camera' | 'library';
/** 9.2.4 — ek menüsünden açılabilecek seçiciler; video da aynı bekleme yolunu kullanıyor. */
type AttachPick = PhotoSource | 'video' | 'voice' | 'location';

/**
 * 9.1.9 — ısmarlanabilen süreler.
 *
 * Serbest bir sayı kutusu DEĞİL ve bu bilerek: emanete girecek tutar seçimin
 * kendisinden doğduğu için, her seçenek yanında ne kadara mal olacağını
 * gösterebilmeli. Tavan serbest gönderimle aynı 60 dakika; sunucu da aynı
 * yerden bakıyor (`isAcceptedDuration`).
 */
const VOICE_ORDER_CHOICES = [60, 180, 300, 600];
type TextBubbleMeasurement = { content: string; width: number };

// 9.22.7 (D-077) — kaydırarak yanıtla. Karar: jest YALNIZ dokunmatik yüzeyde
// çalışır; web'de fare sürüklemesi metin seçimi ve dikey kaydırmayla çakıştığı
// için uzun-basma menüsü orada tek yol olarak kalıyor. RTL dillerde jestin yönü
// yazı yönüyle birlikte çevrilir (I18nManager.isRTL).
const SWIPE_TRIGGER = 56;
const SWIPE_MAX = 80;

function SwipeToReply({
  enabled,
  onReply,
  children,
}: {
  enabled: boolean;
  onReply: () => void;
  children: React.ReactNode;
}) {
  const tx = useRef(new Animated.Value(0)).current;
  // PanResponder bir kez kuruluyor; onReply/enabled her render'da yeniden
  // üretildiği için ref üzerinden okunuyor, yoksa jest ilk render'ın
  // kapanışına takılı kalır.
  const replyRef = useRef(onReply);
  replyRef.current = onReply;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const dir = I18nManager.isRTL ? -1 : 1;

  const pan = useRef(
    PanResponder.create({
      // Yalnız yatay ve yeterince belirgin hareketi üstlen — dikey liste
      // kaydırması FlatList'te kalmalı.
      onMoveShouldSetPanResponder: (_e: any, g: any) =>
        enabledRef.current && g.dx * dir > 10 && Math.abs(g.dx) > Math.abs(g.dy) * 1.5,
      onPanResponderMove: (_e: any, g: any) => {
        const d = Math.max(0, Math.min(g.dx * dir, SWIPE_MAX));
        tx.setValue(d * dir);
      },
      onPanResponderRelease: (_e: any, g: any) => {
        if (g.dx * dir >= SWIPE_TRIGGER) replyRef.current();
        Animated.spring(tx, { toValue: 0, useNativeDriver: true, bounciness: 0 }).start();
      },
      onPanResponderTerminate: () => {
        Animated.spring(tx, { toValue: 0, useNativeDriver: true, bounciness: 0 }).start();
      },
    })
  ).current;

  if (Platform.OS === 'web' || !enabled) return <>{children}</>;
  return (
    <Animated.View style={{ transform: [{ translateX: tx }] }} {...pan.panHandlers}>
      {children}
    </Animated.View>
  );
}

/**
 * Sohbetteki fotoğraf. Köşeler metin balonuyla aynı kavisi taşıyor (7.14).
 *
 * Kavis BALONA değil görselin kendisine veriliyor: balonun içinde, görselin
 * altında konum etiketi de olabiliyor ve balonu kırpmak o yazının köşelerini
 * keserdi. Yüzey zaten görselin kendisi — balon fotoğraf mesajında saydam.
 */
function MessageImage({ url, onLongPress, mine }: {
  url: string;
  onLongPress: () => void;
  mine: boolean;
}) {
  const [height, setHeight] = useState(MSG_IMG_WIDTH * 0.75);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullH, setFullH] = useState(SCREEN_HEIGHT * 0.7);

  useEffect(() => {
    Image.getSize(url, (w, h) => {
      const ratio = h / w;
      setHeight(Math.min(ratio * MSG_IMG_WIDTH, MSG_IMG_MAX_HEIGHT));
      setFullH(Math.min(ratio * SCREEN_WIDTH, SCREEN_HEIGHT * 0.85));
    });
  }, [url]);

  const imageStyle = useMemo(
    () => [
      imgStyles.messageImage,
      { height },
      // Kuyruk köşesi metin balonundaki gibi yöne göre düzleşiyor.
      mine ? imgStyles.messageImageMine : imgStyles.messageImageOther,
    ],
    [height, mine]
  );

  return (
    <>
      <TouchableOpacity
        onPress={() => setFullscreen(true)}
        onLongPress={onLongPress}
        activeOpacity={0.9}>
        <Image
          source={{ uri: url }}
          style={imageStyle}
          resizeMode="cover"
        />
      </TouchableOpacity>

      <Modal visible={fullscreen} transparent animationType="fade" onRequestClose={() => setFullscreen(false)}>
        <TouchableOpacity
          style={imgStyles.overlay}
          activeOpacity={1}
          onPress={() => setFullscreen(false)}>
          <Image
            source={{ uri: url }}
            style={{ width: SCREEN_WIDTH, height: fullH }}
            resizeMode="contain"
          />
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const imgStyles = StyleSheet.create({
  messageImage: {
    width: MSG_IMG_WIDTH,
    borderRadius: radius.card,
  },
  messageImageMine: { borderBottomRightRadius: 4 },
  messageImageOther: { borderBottomLeftRadius: 4 },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.92)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

type TFunc = (key: string, params?: Record<string, string | number>) => string;

function dateLabel(dateStr: string, t: TFunc, lang: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return t('time.today');
  if (d.toDateString() === yesterday.toDateString()) return t('time.dayYesterday');
  return d.toLocaleDateString(lang, { day: 'numeric', month: 'long' });
}

export default function MessageScreen({
  token,
  myId,
  otherId,
  otherName,
  otherPhoto,
  onBack,
  onViewProfile,
  onStartCall,
  paused = false,
  initialAction,
  onInitialActionHandled,
}: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { isDark } = useThemeMode();
  const styles = useMemo(() => makeStyles(colors, isDark), [colors, isDark]);
  const [messages, setMessages] = useState<Message[]>([]);
  // API konuşmayı kronolojik döndürür. Sohbet listesi ters çalıştığı için en
  // yeni kayıt veri dizisinin ilk elemanı olur; böylece ilk karede ölçüm veya
  // sonradan scroll gerekmeden doğrudan son mesaj görünür.
  // D-306 — eski API/önbellek bir `price` satırı taşısa bile sohbet akışına
  // sokma. Fiyat geçmişi denetim kaydıdır; kullanıcıya güncel tutar kalıcı
  // sohbet özeti ve gönderim/arama fiyat yüzeylerinde gösterilir.
  const messagesNewestFirst = useMemo(
    () => messages.filter((message) => message.type !== 'price').reverse(),
    [messages],
  );
  // D-666 — konuşmanın duygusu (Plutchik), yalnız bu cihazda ve yalnız mesajlar
  // zaten yüklüyken hesaplanır: gelen kutusunu boyamak için arka planda konuşma
  // çekmek okundu bilgisi gönderebilirdi. Sonuç (tek bir duygu adı) bu hesap
  // için saklanır; gelen kutusu oradan okur.
  const peerMood = useMemo(() => conversationMood(messagesNewestFirst), [messagesNewestFirst]);

  // 5.4.2 — "Görüldü HH:MM". Her balonun altına değil, karşı tarafın gördüğü
  // EN SON mesajımın altına tek satır (iMessage/Telegram deseni): aynı bilgiyi
  // her mesajda tekrarlamak sohbeti okunmaz hâle getirirdi.
  //
  // Yalnız `read_at` varsa çizilir. `status === 'replied'` de okundu sayılıp çift
  // tik veriyor ama GÖRÜLME ANINI taşımıyor; saati olmayan bir "görüldü" satırı
  // yazmayız. Karşılıklı gizlilik kapalıysa sunucu `read_at`'i zaten null
  // döndürüyor (5.4.3), yani bu satır gizlilik ayarına kendiliğinden uyar.
  const lastSeenMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.sender_id === myId && isRealMessage(m) && m.read_at && m.status !== 'cancelled') {
        return m.id;
      }
    }
    return null;
  }, [messages, myId]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  // D-666 — hesaplanan konuşma duygusu, mesajlar yüklendikten sonra saklanır.
  useEffect(() => {
    if (loading) return;
    storage.setAvatarMood(myId, otherId, peerMood).catch(() => {});
  }, [loading, peerMood, myId, otherId]);
  const [sending, setSending] = useState(false);
  const [otherLastSeen, setOtherLastSeen] = useState<string | null>(null);
  // 9.26 / D-641 — karşı taraf resmî hesap mı (başlıkta mavi işaret).
  const [otherIsOfficial, setOtherIsOfficial] = useState(false);
  // 9.34 / D-661 — bu konuşmada yazılan şifreli gidiyor mu. Düz metne geri
  // düşüş artık sessiz değil: şifresizse başlığın altında söylenir. Açılışta
  // sorulur, her gönderimin sonucu da durumu günceller.
  const [encryption, setEncryption] = useState<EncryptionState>('unknown');
  useEffect(() => {
    let active = true;
    setEncryption('unknown');
    conversationEncryption(token, otherId)
      .then((state) => { if (active) setEncryption(state); })
      .catch(() => {});
    return () => { active = false; };
  }, [token, otherId]);
  const [otherPriceTime, setOtherPriceTime] = useState<{ price: number | string; currency: string } | null>(null);
  // 9.1.6b — taksimetrenin gösterdiği tutar karşı tarafın DAKİKA ücretinden
  // çıkıyor; serbest gönderimde ödeyen kaydı yapan kişi.
  const [otherPriceVoice, setOtherPriceVoice] = useState<{ price: number; currency: string } | null>(null);
  /**
   * 9.1.9 — ses dakika ücretinin ÖDEYEN para biriminden gelip gelmediği.
   *
   * İki kaynak var ve aynı değişkene yazıyorlar: profil satıcının İLAN ETTİĞİ
   * fiyatı satıcının para biriminde veriyor, fiyat ucu ise ödeyenin cebinden
   * çıkacak tutarı. İkincisi geldikten sonra birincisinin üstüne yazması,
   * ekranda cüzdanla ilgisi olmayan bir sayı bırakıyordu — sipariş listesi
   * konuşmanın ₺ fiyat satırının altında $ gösteriyordu. Ödeyen sürüm bir kez
   * geldiyse profil artık onu ezemez.
   */
  const odeyenSesFiyatiGeldi = useRef(false);
  // 9.3.3.2 — bu kişiyle açık canlı paylaşım. Ekran açılışta soruluyor:
  // süresiz paylaşım mümkün olduğu için (D-575) kullanıcı uygulamayı kapatıp
  // açtığında paylaşımının sürdüğünü GÖRMEK zorunda.
  const [liveShare, setLiveShare] = useState<LocationShare | null>(null);
  const [liveShareMenu, setLiveShareMenu] = useState(false);
  // İzleyenin açtığı panel. Açık kaldığı sürece konumu soruyor; sorma aynı
  // zamanda paylaşan cihaza "biri bakıyor" diyen sinyal (9.3.3.4).
  const [watchedShareId, setWatchedShareId] = useState<number | null>(null);
  const [otherPriceScheduledCall, setOtherPriceScheduledCall] = useState<{ price: number | string; currency: string } | null>(null);
  // 3.10.5 — current quote stays in the conversation's opening summary.
  // The sending ceiling advances only on initial display, a decrease or consent.
  const [messagePrice, setMessagePrice] = useState<MessagePriceQuote | null>(null);
  // Polling may discover an increase, but is never consent to pay it.
  const messagePriceLimitRef = useRef<MessagePriceQuote | null>(null);
  // 3.10.5 — anlık aramanın dakika ücreti. Kişiye özel fiyatı da içeren bu kota
  // yalnız "Şimdi ara" akışınındır; planlı teklif D-299 ile aşağıdaki ayrı
  // kotayı kullanır.
  const [callPrice, setCallPrice] = useState<CallPriceQuote | null>(null);
  const callPriceRef = useRef<CallPriceQuote | null>(null);
  // D-299 — planlı çağrı ayrı genel fiyat taşır. Teklif bu kotanın satıcı
  // tarafını dondurur; anlık aramanın `callPrice` değeriyle karıştırılmaz.
  const [scheduledCallPrice, setScheduledCallPrice] = useState<CallPriceQuote | null>(null);
  // 9.4.14 — görüntülü aramanın kendi kotası. `callPrice` ile karıştırılmaz:
  // iki kip ayrı ücret taşır ve damga hangi kipe basıldıysa o ücretle gider.
  const [videoCallPrice, setVideoCallPrice] = useState<CallPriceQuote | null>(null);
  const videoCallPriceRef = useRef<CallPriceQuote | null>(null);
  // Teklif, SATICININ ücretini dondurur (D-252) ve kur teklif anında sabitlenmez;
  // bu yüzden rezervasyon ekranı satıcı tarafını gösterir. Kota yüklenemezse
  // profilden gelen genel ücrete düşer — eski davranış.
  const callPriceForReservation = scheduledCallPrice
    ? { amount: Number(scheduledCallPrice.seller_amount), currency: scheduledCallPrice.seller_currency }
    : otherPriceScheduledCall
      ? { amount: Number(otherPriceScheduledCall.price), currency: otherPriceScheduledCall.currency }
      : null;
  const { lang: targetLang, t } = useTranslation();
  const applyMessagePrice = useCallback((quote: MessagePriceQuote | null, accepted = false) => {
    const limit = messagePriceLimitRef.current;
    const decreased = quote && limit && quote.currency === limit.currency
      && Number(quote.amount) < Number(limit.amount);
    if (!quote || !limit || accepted || decreased) messagePriceLimitRef.current = quote;
    setMessagePrice(quote);
    // 9.1.6 — taksimetre ÖDEYENİN para biriminden besleniyor. Profildeki
    // `price_voice` satıcının ilan ettiği sayıdır; cüzdandan çıkan tutar o
    // değildir. Eski sunucu bu alanı vermiyorsa profilden gelen değer kalıyor.
    if (quote?.voice_per_minute != null && quote.voice_currency) {
      odeyenSesFiyatiGeldi.current = true;
      setOtherPriceVoice({ price: Number(quote.voice_per_minute), currency: quote.voice_currency });
    }
    if (decreased && !accepted) {
      Alert.alert(t('message.priceDecreasedTitle'), t('message.priceDecreasedBody', {
        old: formatMoney(Number(limit.amount), limit.currency, targetLang),
        new: formatMoney(Number(quote.amount), quote.currency, targetLang),
      }));
    }
  }, [t, targetLang]);
  const [otherIsTyping, setOtherIsTyping] = useState(false);
  const [translateOn, setTranslateOn] = useState(false);
  const [translations, setTranslations] = useState<Record<number, string>>({});
  const [textBubbleMeasurements, setTextBubbleMeasurements] = useState<
    Record<number, TextBubbleMeasurement>
  >({});
  const translationContextRef = useRef(`${otherId}:${targetLang}`);
  const pendingTranslationKeysRef = useRef(new Set<string>());
  translationContextRef.current = `${otherId}:${targetLang}`;
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  // 9.32 / D-653, D-655 — yazılan linkin nasıl gideceği. Varsayılan ayarlardan
  // gelir; seçilmemişse KISA (Batuhan'ın kararı). Kısa ya da Büyük kart
  // varsayılansa önizleme, link yazılınca bu telefonda kendiliğinden hazırlanır
  // ve şifreli mesajın içinde gider; şifresiz konuşmada metinden ayrı bir
  // alanda gider (D-658). Açık adres hiçbir sayfa açmaz. Yazarken
  // yalnız DİĞER iki seçenek görünür (ayardan kapatılabilir). Web'de seçenek
  // yok: tarayıcı başka bir sitenin sayfasını okuyamaz.
  const [linkPrefs, setLinkPrefs] = useState<{ view: 'plain' | LinkView; ask: boolean } | null>(null);
  const [linkOverride, setLinkOverride] = useState<'plain' | LinkView | null>(null);
  const [linkPreview, setLinkPreview] = useState<LinkPreview | null>(null);
  const [linkPreviewLoading, setLinkPreviewLoading] = useState(false);
  const [linkPreviewFailed, setLinkPreviewFailed] = useState(false);
  const draftLink = useMemo(() => (Platform.OS === 'web' ? null : firstPreviewableLink(text)), [text]);
  const draftLinkRef = useRef(draftLink);
  draftLinkRef.current = draftLink;
  // Tercih okunana kadar hiçbir sayfa açılmaz.
  const linkView: 'plain' | LinkView = linkOverride ?? linkPrefs?.view ?? 'plain';
  useEffect(() => {
    let active = true;
    Promise.all([storage.getLinkViewDefault(myId), storage.getLinkViewAsk(myId)])
      .then(([view, ask]) => { if (active) setLinkPrefs({ view, ask }); })
      .catch(() => { if (active) setLinkPrefs({ view: 'compact', ask: true }); });
    return () => { active = false; };
  }, [myId]);
  useEffect(() => {
    // Link değiştiyse eski önizleme yeni linke ait değil.
    if (linkPreview && linkPreview.url !== draftLink) setLinkPreview(null);
    if (!draftLink) {
      if (linkOverride !== null) setLinkOverride(null);
      if (linkPreviewFailed) setLinkPreviewFailed(false);
    }
  }, [draftLink, linkPreview, linkOverride, linkPreviewFailed]);
  useEffect(() => {
    const url = draftLink;
    if (!url || linkView === 'plain') return;
    // Büyük kart her şeyi taşıyor; kısa görünüm için yeniden istek gerekmez.
    if (linkPreview?.url === url && (linkView === 'compact' || linkPreview.view === 'card')) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setLinkPreviewFailed(false);
      setLinkPreviewLoading(true);
      buildLinkPreview(url, linkView)
        .then((built) => {
          // Beklerken link değiştiyse bu sonuç artık kimseye ait değil.
          if (cancelled || draftLinkRef.current !== url) return;
          if (built) setLinkPreview(built);
          else setLinkPreviewFailed(true);
        })
        .catch(() => { if (!cancelled) setLinkPreviewFailed(true); })
        .finally(() => { if (!cancelled) setLinkPreviewLoading(false); });
    }, LINK_PREVIEW_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setLinkPreviewLoading(false);
    };
  }, [draftLink, linkView, linkPreview]);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [actionTarget, setActionTarget] = useState<Message | null>(null);
  const [attachMenu, setAttachMenu] = useState(false);
  const [meetupModal, setMeetupModal] = useState(false);
  const [meetupDate, setMeetupDate] = useState(() => new Date(Date.now() + 3600_000));
  const [meetupLocation, setMeetupLocation] = useState('');
  const [meetupDurationText, setMeetupDurationText] = useState('30');
  const meetupDuration = parseDurationInput(meetupDurationText);
  const [meetupDateValid, setMeetupDateValid] = useState(true);
  const [meetupSubmitting, setMeetupSubmitting] = useState(false);
  const [callActionMenu, setCallActionMenu] = useState(false);
  const [callReservationModal, setCallReservationModal] = useState(false);
  const [callReservationDate, setCallReservationDate] = useState(() => new Date(Date.now() + 3600_000));
  const [callReservationDurationText, setCallReservationDurationText] = useState('30');
  const callReservationDuration = parseDurationInput(callReservationDurationText);
  const [callReservationDateValid, setCallReservationDateValid] = useState(true);
  const [callReservationSubject, setCallReservationSubject] = useState('');
  const [callReservationSubmitting, setCallReservationSubmitting] = useState(false);
  const typingThrottle = useRef<number | null>(null);
  const listRef = useRef<FlatList>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const photoPickerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ekran kapanırken açık kalan zamanlayıcılar temizlenir: "yazıyor" kısıtı
  // (2 sn), fotoğraf seçici gecikmesi ve vurgu sönümü. Üçü de sökülmeden sonra
  // tetiklenirse boşuna çalışır ve sökülmüş bileşenin state'ine dokunmaya
  // çalışır; "yazıyor" kısıtının hiç temizliği yoktu.
  useEffect(() => () => {
    if (typingThrottle.current) clearTimeout(typingThrottle.current);
    if (photoPickerTimer.current) clearTimeout(photoPickerTimer.current);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
  }, []);
  const photoPickerPending = useRef<AttachPick | null>(null);
  // Çağrıdan dönünce mevcut balonları boşaltmadan güncelle; kullanıcı yalnız
  // gerçekten başka bir konuşmaya geçtiğinde ilk-yükleme görünümünü görür.
  const loadedConversationRef = useRef<string | null>(null);

  const translateAll = useCallback(async (msgs: Message[]) => {
    // Yalnız gerçek kullanıcı metni çevrilir. Çağrı/buluşma gibi sentetik JSON
    // içerikleri ve zaten çevrilmiş mesajlar sağlayıcıya gönderilmez.
    const context = `${otherId}:${targetLang}`;
    const items = msgs.filter((m) => {
      const pendingKey = `${context}:${m.id}`;
      return m.type === 'text'
        && m.status !== 'cancelled'
        && !!m.content
        && !(m.id in translations)
        && !pendingTranslationKeysRef.current.has(pendingKey);
    });
    if (items.length === 0) return;
    const pendingKeys = items.map((item) => `${context}:${item.id}`);
    pendingKeys.forEach((key) => pendingTranslationKeysRef.current.add(key));
    try {
      const { translations: result } = await api.translateMessages(
        token,
        items.map((m) => m.id),
        targetLang
      );
      // Kullanıcı bu sırada dili veya konuşmayı değiştirdiyse geç kalan yanıtın
      // yeni ekrana yazılmasına izin verme.
      if (translationContextRef.current !== context) return;
      setTranslations((current) => {
        const next = { ...current };
        result.forEach((translation) => { next[translation.message_id] = translation.text; });
        return next;
      });
    } catch {
      // Sessizce başarısız — toggle açık kalır, orijinal metin gösterilir
    } finally {
      pendingKeys.forEach((key) => pendingTranslationKeysRef.current.delete(key));
    }
  }, [otherId, targetLang, token, translations]);

  useEffect(() => {
    setTranslations({});
  }, [otherId, targetLang]);

  useEffect(() => {
    let active = true;
    setTranslateOn(false);
    loadContactTranslationPreference(token, myId, otherId)
      .then((enabled) => {
        if (active) setTranslateOn(enabled);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [token, myId, otherId]);

  useEffect(() => {
    if (translateOn) translateAll(messages);
    // Toggle açıldığında ve yeni mesaj geldiğinde yeniden çevir
  }, [messages, translateAll, translateOn]);

  function openMeetupModal() {
    setMeetupDate(new Date(Date.now() + 3600_000));
    setMeetupDateValid(true);
    setMeetupLocation('');
    setMeetupDurationText('30');
    setMeetupModal(true);
  }

  useEffect(() => {
    if (!initialAction) return;
    if (initialAction === 'meetup') openMeetupModal();
    if (initialAction === 'callReservation') openCallReservationModal();
    // 9.28 — "şimdi başlıyor" bildiriminden gelindi. Arama dakika başına
    // ücretli olduğu için doğrudan başlatılmaz; kullanıcı açıkça onaylar.
    if (typeof initialAction === 'object' && initialAction.type === 'callNow') {
      const reservationId = initialAction.reservationId;
      Alert.alert(
        t('call.scheduledStartTitle'),
        t('call.scheduledStartBody', { name: otherName }),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('call.callNow'), onPress: () => onStartCall?.(reservationId) },
        ]
      );
    }
    onInitialActionHandled?.();
  }, [initialAction, onInitialActionHandled, onStartCall, otherName, t]);

  // Not: bu, api.sendMessage'i ÇAĞIRMAZ — sohbete hiçbir mesaj balonu eklenmez,
  // escrow'a dokunulmaz (D-027, 9.3.2 kararı: ödeme bağlantısı 9.3.5, ayrı ve
  // henüz yapılmadı). Karşı taraf teklifi yalnızca Takvim sekmesinden görür.
  /**
   * 3.10.5 — teklif reddiyse fiyat değişimi mi? Öyleyse ekrandaki ücreti
   * tazeleyip kullanıcıya nedenini söyleriz; teklif oluşmadı, para hareket
   * etmedi. Mesajdaki gibi sessiz yeniden deneme yok: rezervasyon zaman ve süre
   * seçimi taşır, kullanıcı yeni ücreti görüp bilerek yeniden teklif etmelidir.
   */
  function handledPriceChange(err: unknown): boolean {
    const current = err instanceof ApiError && err.status === 409 && err.code === 'price_changed'
      ? err.details?.current
      : null;
    if (!current || typeof current.amount !== 'number') return false;
    api.getCallPrice(token, otherId)
      .then((quote) => { callPriceRef.current = quote; setCallPrice(quote); })
      .catch(() => {});
    api.getScheduledCallPrice(token, otherId)
      .then(setScheduledCallPrice)
      .catch(() => {});
    api.getVideoCallPrice(token, otherId)
      .then((quote) => { videoCallPriceRef.current = quote; setVideoCallPrice(quote); })
      .catch(() => {});
    api.getUser(otherId, undefined, token).then((u: any) => {
      if (u?.price_time != null) setOtherPriceTime({ price: u.price_time, currency: u.currency });
      if (u?.price_voice != null && !odeyenSesFiyatiGeldi.current) {
        setOtherPriceVoice({ price: Number(u.price_voice), currency: u.currency });
      }
      if (u?.price_call_scheduled != null) {
        setOtherPriceScheduledCall({ price: u.price_call_scheduled, currency: u.currency });
      }
    }).catch(() => {});
    Alert.alert(
      t('message.priceChangedTitle'),
      t('calendar.priceChangedBody', {
        amount: formatMoney(Number(current.amount), current.currency, targetLang),
      })
    );
    return true;
  }

  async function submitMeetup() {
    if (meetupDuration === null || !meetupDateValid || meetupSubmitting) return;
    setMeetupSubmitting(true);
    try {
      const durationMinutes = meetupDuration;
      const continueWithConflict = await confirmCalendarConflict(
        token,
        myId,
        meetupDate,
        durationMinutes,
        targetLang,
        t
      );
      if (!continueWithConflict) return;
      await api.proposeMeetup(
        token, otherId, meetupDate.toISOString(),
        meetupLocation.trim() || undefined, durationMinutes,
        otherPriceTime
          ? { amount: Number(otherPriceTime.price), currency: otherPriceTime.currency }
          : null
      );
      setMeetupModal(false);
      // Teklif artık sohbette bir sistem mesajı olarak da göründüğü için
      // listeyi hemen tazeliyoruz — sonraki 5sn'lik poll'u beklemeye gerek yok.
      e2eeGetMessages(token, otherId)
        .then((next) => setMessages((prev) => mergeFetched(prev, next)))
        .catch(() => {});
      Alert.alert(t('calendar.proposalSent'), t('calendar.proposalSentBody', { name: otherName }));
    } catch (err) {
      if (!handledPriceChange(err)) {
        Alert.alert(t('common.error'), t('calendar.proposalFailed'));
      }
    } finally {
      setMeetupSubmitting(false);
    }
  }

  function openCallReservationModal() {
    setCallReservationDate(new Date(Date.now() + 3600_000));
    setCallReservationDateValid(true);
    setCallReservationDurationText('30');
    setCallReservationModal(true);
  }

  async function submitCallReservation() {
    if (callReservationDuration === null || !callReservationDateValid || callReservationSubmitting) return;
    setCallReservationSubmitting(true);
    try {
      const durationMinutes = callReservationDuration;
      const continueWithConflict = await confirmCalendarConflict(
        token,
        myId,
        callReservationDate,
        durationMinutes,
        targetLang,
        t
      );
      if (!continueWithConflict) return;
      // 3.10.5 — teklif ekranında gösterilen ücret damga olarak gider; donacak
      // ücret ondan yüksekse sunucu teklifi hiç oluşturmaz.
      await api.proposeCallReservation(
        token, otherId, callReservationDate.toISOString(), durationMinutes,
        callReservationSubject.trim() || undefined,
        callPriceForReservation
      );
      setCallReservationModal(false);
      setCallReservationSubject('');
      e2eeGetMessages(token, otherId)
        .then((next) => setMessages((prev) => mergeFetched(prev, next)))
        .catch(() => {});
      Alert.alert(t('call.reservationSent'), t('call.reservationSentBody', { name: otherName }));
    } catch (err) {
      if (!handledPriceChange(err)) {
        Alert.alert(t('common.error'), t('call.reservationFailed'));
      }
    } finally {
      setCallReservationSubmitting(false);
    }
  }

  // Tam ekran çağrı MessageScreen'i unmount etmez. Çağrı sürerken görünmeyen
  // sohbetin mesaj, son görülme ve yazıyor yoklamaları (en çok 34 istek/dk)
  // durur; dönüşte konuşma silinmeden hemen güncellenir.
  // Sohbet değişirse ödeyen fiyatı yeniden sorulacak; eski konuşmadan kalan
  // bir "geldi" işareti, yeni konuşmanın profil fiyatını da susturur.
  useEffect(() => { odeyenSesFiyatiGeldi.current = false; }, [otherId]);

  useEffect(() => {
    if (paused) {
      setOtherIsTyping(false);
      return;
    }

    let active = true;
    const conversationKey = `${token}:${otherId}`;
    const firstLoad = loadedConversationRef.current !== conversationKey;
    loadedConversationRef.current = conversationKey;

    const refreshMessages = (finishLoading = false) => {
      // 9.34 / D-634 — kimlik denetimi yalnız sohbet AÇILIRKEN; yoklamada değil.
      e2eeGetMessages(token, otherId, finishLoading)
        .then((next) => { if (active) setMessages((prev) => mergeFetched(prev, next)); })
        .catch(() => {})
        .finally(() => {
          if (active && finishLoading) setLoading(false);
        });
    };
    const refreshUser = () => {
      api.getUser(otherId, undefined, token).then((u: any) => {
        if (!active) return;
        if (u?.last_seen) setOtherLastSeen(u.last_seen);
        setOtherIsOfficial(u?.is_official === true);
        // Postgres numeric kolonları pg sürücüsünden string olarak gelir (ör. "5.00"),
        // number değil — bu yüzden typeof kontrolü değil, null/undefined kontrolü.
        if (u?.price_time != null) setOtherPriceTime({ price: u.price_time, currency: u.currency });
        if (u?.price_voice != null && !odeyenSesFiyatiGeldi.current) {
          setOtherPriceVoice({ price: Number(u.price_voice), currency: u.currency });
        }
        if (u?.price_call_scheduled != null) {
          setOtherPriceScheduledCall({ price: u.price_call_scheduled, currency: u.currency });
        }
      }).catch(() => {});
      api.getMessagePrice(token, otherId)
        .then((quote) => { if (active) applyMessagePrice(quote); })
        .catch(() => {});
      api.activeLocationShare(token, otherId)
        .then((r) => {
          if (!active) return;
          // Aynı paylaşım için YENİ bir nesne koymuyoruz. Koysaydık ölçüm
          // döngüsünün bağımlılığı her yoklamada değişir, döngü baştan başlar
          // ve ilk nokta koşulsuz gönderilirdi: bakan yokken 120 saniyede bir
          // ölçmesi gereken cihaz 30 saniyede bir gönderiyordu. Kayıtlarda
          // böyle görüldü.
          setLiveShare((onceki) => {
            const yeni = r.share;
            // `last_viewed_at` de karşılaştırılıyor: bakıldığında çubuk
            // tazelenmeli. Döngü kimliğe bağlı olduğu için (D-584) bu
            // değişiklik ölçümü yeniden başlatmıyor.
            if (onceki?.id === yeni?.id && onceki?.status === yeni?.status
              && onceki?.expires_at === yeni?.expires_at
              && onceki?.last_viewed_at === yeni?.last_viewed_at) return onceki;
            return yeni;
          });
        })
        .catch(() => {});
      api.getCallPrice(token, otherId)
        .then((quote) => {
          if (!active) return;
          callPriceRef.current = quote;
          setCallPrice(quote);
        })
        .catch(() => {});
      api.getScheduledCallPrice(token, otherId)
        .then((quote) => {
          if (active) setScheduledCallPrice(quote);
        })
        .catch(() => {});
      api.getVideoCallPrice(token, otherId)
        .then((quote) => {
          if (!active) return;
          videoCallPriceRef.current = quote;
          setVideoCallPrice(quote);
        })
        .catch(() => {});
    };

    if (firstLoad) {
      setLoading(true);
      setMessages([]);
      applyMessagePrice(null);
    }
    refreshMessages(firstLoad);
    refreshUser();

    const msgInterval = setInterval(refreshMessages, 5000);
    const seenInterval = setInterval(refreshUser, 30000);
    const typingInterval = setInterval(() => {
      api.getTyping(token, otherId)
        .then((r) => { if (active) setOtherIsTyping(r.isTyping); })
        .catch(() => {});
    }, 3000);

    return () => {
      active = false;
      clearInterval(msgInterval);
      clearInterval(seenInterval);
      clearInterval(typingInterval);
    };
  }, [applyMessagePrice, otherId, paused, token]);

  async function sendPhoto(source: PhotoSource) {
    let result: ImagePicker.ImagePickerResult;
    try {
      if (source === 'camera') {
        const permission = await ImagePicker.requestCameraPermissionsAsync();
        if (!permission.granted) {
          Alert.alert(t('message.cameraPermissionTitle'), t('message.cameraPermissionBody'));
          return;
        }
        result = await ImagePicker.launchCameraAsync({
          mediaTypes: ['images'],
          quality: 0.8,
          exif: true,
        });
      } else {
        result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ['images'],
          quality: 0.8,
          exif: true,
          // iOS Photos, HEIC/iCloud varlığını ağ katmanının okuyabileceği yerel ve
          // uyumlu bir temsile dışa aktarır. Android bu iOS seçeneğini yok sayar.
          preferredAssetRepresentationMode:
            ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
        });
      }
    } catch {
      Alert.alert(
        t('common.error'),
        source === 'camera' ? t('message.cameraError') : `${t('message.photoError')} (P1)`
      );
      return;
    }
    if (result.canceled) return;
    const asset = result.assets[0];
    if (!asset?.uri) {
      Alert.alert(t('common.error'), `${t('message.photoError')} (P2)`);
      return;
    }
    setSending(true);
    const replyTarget = replyingTo;
    setReplyingTo(null);
    try {
      // Galeri fotoğrafında yalnız dosya GPS'i çekim yerine delildir. Doğrudan
      // kamerada iOS GPS EXIF vermediği için, başarılı çekimin hemen ardından
      // yalnız foreground izinle cihaz konumu okunur. İki kaynak karıştırılmaz.
      const coordinates = source === 'camera'
        ? await coordinatesAtCapture()
        : coordinatesFromExif(asset.exif);
      const locationSource = source === 'camera' ? 'capture_location' : 'file_metadata';
      const { url, location } = await api.uploadMessagePhoto(
        token,
        asset.uri,
        asset.file,
        coordinates ?? undefined,
        targetLang,
        locationSource
      );
      let locationVisible = true;
      if (location) {
        const place = [location.district, location.city].filter(Boolean).join(' · ');
        const choice = await new Promise<'show' | 'hide' | 'cancel'>((resolve) => {
          Alert.alert(
            t('message.photoLocationTitle'),
            t(location.source === 'capture_location'
              ? 'message.photoLocationPromptCapture'
              : 'message.photoLocationPromptFile', { place }),
            [
              { text: t('message.photoLocationHide'), onPress: () => resolve('hide') },
              { text: t('common.cancel'), style: 'cancel', onPress: () => resolve('cancel') },
              { text: t('message.photoLocationShare'), onPress: () => resolve('show') },
            ],
            { cancelable: false }
          );
        });
        if (choice === 'cancel') {
          setReplyingTo(replyTarget);
          return;
        }
        locationVisible = choice === 'show';
      }
      const msg = await sendStamped((stamp) => api.sendMessage(
        token,
        otherId,
        url,
        'image',
        replyTarget?.id,
        locationVisible,
        stamp
      ));
      if (!msg) {
        setReplyingTo(replyTarget);
        return;
      }
      setMessages((prev) => upsertLocal(prev, msg));
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    } catch (err) {
      setReplyingTo(replyTarget);
      if (err instanceof ApiError && err.status === 402) {
        Alert.alert(t('message.lowBalanceTitle'), t('message.lowBalanceBody'));
      } else {
        const detail = err instanceof ApiError ? String(err.status) : err instanceof NetworkError ? 'NET' : 'UNK';
        Alert.alert(t('common.error'), `${t('message.photoError')} (P3-${detail})`);
      }
    } finally {
      setSending(false);
    }
  }

  /**
   * Video gönderimi (9.2.4 · D-563).
   *
   * Fotoğraftan ayrı bir işlev, çünkü yol da ayrı: fotoğraf dosyayı API'ye
   * yolluyor, video ise bilet alıp baytları doğrudan depoya yazıyor. Sıranın
   * kendisi `media/mediaUpload.ts`'te ve orada sınanıyor; burada yalnız
   * seçicinin çıktısı ona bağlanıyor ve hata kullanıcıya çevriliyor.
   */
  const [voiceSheet, setVoiceSheet] = useState(false);

  /**
   * Sipariş yüzeyleri (9.1.9 · 9.1.10 · 9.1.11 · 9.1.12).
   *
   * `siparisTeslim` doluyken kayıt ekranı SİPARİŞ kipindedir: tavanı siparişin
   * süresi, taksimetresi yok. Serbest kayıtla aynı ekranı paylaşıyorlar çünkü
   * ikisi de aynı cihazı kullanıyor; ayıran tek şey paranın kimde olduğudur.
   */
  const [orderMenu, setOrderMenu] = useState(false);
  const [orderBusy, setOrderBusy] = useState(false);
  const [siparisTeslim, setSiparisTeslim] = useState<{ id: number; seconds: number } | null>(null);

  /**
   * Canlı paylaşımı başlatır (9.3.3.2 · D-579). Süre seçilmezse süresizdir.
   *
   * Konum ölçümü burada YOK: bu madde kaydı açıyor, konumu okumak 9.3.3.3 ve
   * 9.3.3.4'e ait. İzin yine de önden isteniyor — paylaşımı açıp sonra izin
   * yokluğundan hiçbir şey gönderememek, açık görünen ölü bir kayıt bırakırdı.
   */
  async function startLiveLocation(minutes: number | null) {
    setLiveShareMenu(false);
    if (!await requestForegroundLocationPermission()) {
      Alert.alert(t('common.error'), t('message.liveLocationError'));
      return;
    }
    try {
      const share = await api.startLocationShare(token, otherId, minutes);
      setLiveShare(share);
      // 9.3.3.7 — arka planda da ölçebilmek için görevin kime ait olduğunu
      // diske yazıyoruz: işletim sistemi uygulamayı uyandırdığında hiçbir
      // ekran kurulmuş olmuyor. Arka plan izni AYRI istenir ve reddedilirse
      // paylaşım yine çalışır, yalnız uygulama öndeyken.
      await rememberLiveShare({ token, shareId: share.id, apiUrl: BASE_URL });
      if (await requestBackgroundLocationPermission()) await startBackgroundLocation();
    } catch {
      Alert.alert(t('common.error'), t('message.liveLocationError'));
    }
  }

  // 9.3.3.3 / 9.3.3.4 — paylaşım açıkken ölçüm döngüsü koşuyor. Döngünün
  // kendisi `media/liveLocationLoop.ts`'te ve orada sınanıyor; burada yalnız
  // paylaşımın ömrüne bağlanıyor.
  useEffect(() => {
    if (!liveShare) return;
    const handle = startLiveLocationLoop({
      readPoint: async () => {
        const nokta = await readLivePoint();
        return nokta ? { ...nokta, accuracy: nokta.accuracy } : null;
      },
      sendPoint: (point) => api.sendLocationPoint(token, liveShare.id, {
        latitude: point.latitude,
        longitude: point.longitude,
        accuracyM: point.accuracy ?? null,
      }),
      wait: (seconds) => new Promise((resolve) => { setTimeout(resolve, seconds * 1000); }),
      now: () => Date.now(),
    });
    return () => handle.stop();
    // Kimlik yeter: aynı paylaşım sürerken döngü yeniden kurulmamalı.
  }, [liveShare?.id, token]);

  async function stopLiveLocation() {
    const acik = liveShare;
    if (!acik) return;
    // Ekran ÖNCE kapanıyor: durdurmaya basan kişi, ağ yavaşken hâlâ
    // paylaşıyormuş gibi görmemeli. Sunucu reddederse geri konuyor.
    setLiveShare(null);
    // Arka plan görevi ÖNCE susturuluyor: silinmezse kapanmış bir paylaşıma
    // nokta göndermeye devam eder ve cihazı boşuna uyandırır.
    await stopBackgroundLocation();
    await forgetLiveShare();
    try {
      await api.stopLocationShare(token, acik.id);
    } catch {
      setLiveShare(acik);
      Alert.alert(t('common.error'), t('message.liveLocationError'));
    }
  }

  /**
   * Konum gönderir (9.3.3.1 · D-576).
   *
   * Tek ölçüm alınır ve biter: canlı paylaşımın süresi, durdurması ve izleyeni
   * burada YOK (onlar 9.3.3.2). Yer adı sunucuda çözülüyor, istemci yalnız
   * koordinatı gönderiyor — fotoğrafta da aynı çözümleyici kullanılıyor.
   */
  async function sendLocation() {
    const nokta = await coordinatesAtCapture();
    if (!nokta) {
      Alert.alert(t('common.error'), t('message.locationError'));
      return;
    }
    setSending(true);
    const replyTarget = replyingTo;
    setReplyingTo(null);
    try {
      const msg = await sendStamped((stamp) => api.sendMessage(
        token, otherId,
        JSON.stringify({ latitude: nokta.latitude, longitude: nokta.longitude }),
        'location', replyTarget?.id, undefined, stamp,
      ));
      if (!msg) { setReplyingTo(replyTarget); return; }
      setMessages((prev) => upsertLocal(prev, msg));
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    } catch {
      Alert.alert(t('common.error'), t('message.locationError'));
    } finally {
      setSending(false);
    }
  }

  /**
   * Kayıt yüzeyini açar (9.1.6b). Mikrofon izni ekrandan ÖNCE isteniyor:
   * kullanıcıyı boş bir kayıt ekranının karşısında bırakmamak için.
   */
  /**
   * Sipariş ver (9.1.9 · D-559).
   *
   * Para bu çağrıda emanete giriyor, yani başarılıysa bakiye ÇOKTAN azalmıştır.
   * Süre burada onaylanıyor ve sunucu ücreti orada kesiyor: ısmarlayan neye
   * razı olduysa fazlasını ödemiyor, satıcı da sonradan fiyat yükselterek
   * tavanı kaydıramıyor — fiyat siparişte donuyor.
   */
  async function placeVoiceOrder(seconds: number) {
    setOrderMenu(false);
    setOrderBusy(true);
    try {
      await api.createVoiceOrder(token, otherId, seconds);
      // Sipariş satırı sunucuda yazılıyor; listeyi tazelemek onu getiriyor.
      // Yerel olarak uydurmak, kimliği ve son tarihi tahmin etmek olurdu.
      const taze = await e2eeGetMessages(token, otherId);
      setMessages((prev) => mergeFetched(prev, taze));
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    } catch (error) {
      // 402 tek başına anlamlı: cüzdanda para yok demek, bir arıza değil.
      const yetersiz = (error as { status?: number })?.status === 402;
      Alert.alert(
        t('common.error'),
        t(yetersiz ? 'message.voiceOrderInsufficient' : 'message.voiceOrderError'),
      );
    } finally {
      setOrderBusy(false);
    }
  }

  /**
   * Teslim (9.1.11 · D-572).
   *
   * Kayıt genel medya yolundan yükleniyor, sonra siparişe bağlanıyor. Süreyi
   * İSTEMCİ BİLDİRMİYOR: sunucu kaptan ölçüyor. Burada beyan eden taraf satıcı
   * ve uzun beyan etmek satıcıya para kazandırırdı.
   */
  async function deliverOrder(recording: VoiceRecording) {
    const siparis = siparisTeslim;
    setSiparisTeslim(null);
    if (!siparis) return;
    setSending(true);
    try {
      const { url } = await uploadMedia({
        requestTicket: async (input) => {
          const bilet = await api.createMediaTicket(token, input);
          return { uploadUrl: bilet.upload_url, url: bilet.url };
        },
        putFile: ({ uri, uploadUrl, contentType }) => api.uploadMediaBytes(uri, uploadUrl, contentType),
        finalize: (input) => api.finalizeMedia(token, input),
      }, {
        kind: 'voice',
        uri: recording.uri,
        contentType: 'audio/mp4',
        sizeBytes: readFileSizeBytes(recording.uri),
        durationSeconds: recording.seconds,
      });
      await api.deliverVoiceOrder(token, siparis.id, url);
      // İki satır birden değişiyor: yeni ses kaydı geliyor ve sipariş kartının
      // durumu "teslim edildi"ye dönüyor. Tek mesaj eklemek kartı bekliyor
      // bırakırdı.
      const taze = await e2eeGetMessages(token, otherId);
      setMessages((prev) => mergeFetched(prev, taze));
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    } catch {
      Alert.alert(t('common.error'), t('message.voiceOrderDeliverError'));
    } finally {
      setSending(false);
    }
  }

  /** Reddet (9.1.12). Emanetin tamamı ısmarlayana döner, komisyon kesilmez. */
  async function declineOrder(orderId: number) {
    setOrderBusy(true);
    try {
      await api.declineVoiceOrder(token, orderId);
      const taze = await e2eeGetMessages(token, otherId);
      setMessages((prev) => mergeFetched(prev, taze));
    } catch {
      Alert.alert(t('common.error'), t('message.voiceOrderDeclineError'));
    } finally {
      setOrderBusy(false);
    }
  }

  /** Satıcı reddetmeden önce soruluyor: iade geri alınamaz bir adım değil ama
   *  siparişi kapatıyor ve yanlış dokunuşla kapanmamalı. */
  function confirmDecline(orderId: number) {
    Alert.alert(
      t('message.voiceOrderDeclineTitle'),
      t('message.voiceOrderDeclineBody'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('message.voiceOrderDecline'), style: 'destructive', onPress: () => void declineOrder(orderId) },
      ],
    );
  }

  /** Teslim kaydını açar; serbest kayıtla aynı izin kapısından geçiyor. */
  async function openDeliveryRecorder(orderId: number, seconds: number) {
    if (!voiceRecordingAvailable) {
      Alert.alert(t('common.error'), t('message.voiceError'));
      return;
    }
    if (!await requestMicrophonePermission()) {
      Alert.alert(t('message.micPermissionTitle'), t('message.micPermissionBody'));
      return;
    }
    setSiparisTeslim({ id: orderId, seconds });
  }

  async function openVoiceRecorder() {
    if (!voiceRecordingAvailable) {
      Alert.alert(t('common.error'), t('message.voiceError'));
      return;
    }
    if (!await requestMicrophonePermission()) {
      Alert.alert(t('message.micPermissionTitle'), t('message.micPermissionBody'));
      return;
    }
    setVoiceSheet(true);
  }

  /**
   * Kayıt bitti: genel medya yolundan yükleniyor (9.1.1) ve `voice` mesajı
   * olarak gönderiliyor. Video ile aynı dizi — ses için ikinci bir yükleme
   * yolu yok.
   */
  async function sendVoice(recording: VoiceRecording) {
    setVoiceSheet(false);
    setSending(true);
    const replyTarget = replyingTo;
    setReplyingTo(null);
    try {
      const { url } = await uploadMedia({
        requestTicket: async (input) => {
          const bilet = await api.createMediaTicket(token, input);
          return { uploadUrl: bilet.upload_url, url: bilet.url };
        },
        putFile: ({ uri, uploadUrl, contentType }) => api.uploadMediaBytes(uri, uploadUrl, contentType),
        finalize: (input) => api.finalizeMedia(token, input),
      }, {
        kind: 'voice',
        uri: recording.uri,
        contentType: 'audio/mp4',
        sizeBytes: readFileSizeBytes(recording.uri),
        durationSeconds: recording.seconds,
      });
      // Damga, taksimetrenin gösterdiği tutarla mühürlenir. Para birimi
      // BİLİNMİYORSA uydurulmuyor: sabit bir 'USD' yazılmıştı ve cüzdanı lira
      // olan bir kullanıcıda tutarlar eşit (sıfır) olduğu hâlde damga kapısı
      // "fiyat değişti" diyordu — cihazda tam olarak böyle görüldü. Bilinmiyorsa
      // fiyat ucundan isteniyor; o da başarısızsa damga mesaj fiyatına düşüyor.
      let sesFiyat = otherPriceVoice;
      if (!sesFiyat) {
        try {
          const q = await api.getMessagePrice(token, otherId);
          sesFiyat = q.voice_per_minute != null && q.voice_currency
            ? { price: Number(q.voice_per_minute), currency: q.voice_currency }
            : { price: 0, currency: q.currency };
          setOtherPriceVoice(sesFiyat);
        } catch { /* damga aşağıda mesaj fiyatına düşer */ }
      }
      const msg = await sendStampedWith(sesFiyat ? {
        amount: voiceNoteCost(recording.seconds, sesFiyat.price),
        currency: sesFiyat.currency,
      } : null, (stamp) => api.sendMessage(
        token, otherId, url, 'voice', replyTarget?.id, undefined, stamp, recording.seconds,
      ));
      if (!msg) { setReplyingTo(replyTarget); return; }
      setMessages((prev) => upsertLocal(prev, msg));
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    } catch {
      Alert.alert(t('common.error'), t('message.voiceError'));
    } finally {
      setSending(false);
    }
  }

  async function sendVideo() {
    let result: ImagePicker.ImagePickerResult;
    try {
      result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['videos'] });
    } catch {
      Alert.alert(t('common.error'), t('message.videoError'));
      return;
    }
    if (result.canceled) return;
    const asset = result.assets[0];
    if (!asset?.uri) {
      Alert.alert(t('common.error'), t('message.videoError'));
      return;
    }

    // Seçicinin süresi milisaniye; sunucu ve sınırlar saniye konuşuyor.
    const durationSeconds = typeof asset.duration === 'number'
      ? Math.round(asset.duration / 1000)
      : undefined;
    const contentType = asset.mimeType === 'video/quicktime' ? 'video/quicktime' : 'video/mp4';

    setSending(true);
    const replyTarget = replyingTo;
    setReplyingTo(null);
    try {
      const { url } = await uploadMedia({
        requestTicket: async (input) => {
          // Sunucu snake_case konuşuyor; dizinin sözleşmesi camelCase.
          const bilet = await api.createMediaTicket(token, input);
          return { uploadUrl: bilet.upload_url, url: bilet.url };
        },
        putFile: ({ uri, uploadUrl, contentType: type }) => api.uploadMediaBytes(uri, uploadUrl, type),
        finalize: (input) => api.finalizeMedia(token, input),
      }, {
        kind: 'video',
        uri: asset.uri,
        contentType,
        sizeBytes: asset.fileSize ?? readFileSizeBytes(asset.uri),
        durationSeconds,
      });

      const msg = await sendStamped((stamp) => api.sendMessage(
        token, otherId, url, 'video', replyTarget?.id, undefined, stamp, durationSeconds,
      ));
      if (!msg) {
        // Kullanıcı yeni fiyatı onaylamadı. Yükleme zaten tamamlandı ve bilet
        // damgalı; bağlanmadığı için 4.7.6.2'nin saklama süresi onu topluyor.
        setReplyingTo(replyTarget);
        return;
      }
      setMessages((prev) => upsertLocal(prev, msg));
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    } catch (error) {
      // Sınır hatası kullanıcının düzeltebileceği bir şey; diğerleri değil.
      const failure = (error as MediaUploadError)?.failure;
      if (failure?.step === 'limit' && failure.reason !== 'empty') {
        Alert.alert(
          t('common.error'),
          failure.reason === 'too_large' ? t('message.videoTooLarge') : t('message.videoTooLong'),
        );
      } else {
        Alert.alert(t('common.error'), t('message.videoError'));
      }
    } finally {
      setSending(false);
    }
  }

  function openPhotoPickerAfterAttachMenu(source: AttachPick) {
    photoPickerPending.current = source;
    setAttachMenu(false);

    // React Native Modal.onDismiss yalnız iOS'ta güvenilir biçimde çalışır.
    // Android'de galeriyi kapanmakta olan native modalın üstüne sunmamak için
    // kısa fade animasyonunun tamamlanmasını bekliyoruz.
    if (Platform.OS !== 'ios') {
      photoPickerTimer.current = setTimeout(() => {
        launchPendingPhotoPicker();
      }, 350);
    }
  }

  function launchPendingPhotoPicker() {
    // onDismiss ve Android zamanlayıcısı ileride aynı platformda birlikte
    // çalışsa bile sistem seçicisi yalnız bir kez açılır.
    const source = photoPickerPending.current;
    if (!source) return;
    photoPickerPending.current = null;
    if (source === 'video') sendVideo();
    else if (source === 'voice') openVoiceRecorder();
    else if (source === 'location') sendLocation();
    else sendPhoto(source);
  }

  useEffect(() => () => {
    if (photoPickerTimer.current) clearTimeout(photoPickerTimer.current);
  }, []);

  function reservationPreview(content: string) {
    try {
      const data = JSON.parse(content);
      if (data?.changeType === 'reschedule') {
        return t(data.kind === 'call'
          ? 'call.rescheduleBubbleShort'
          : 'calendar.rescheduleBubbleShort');
      }
      return data?.kind === 'call'
        ? t('call.reservationBubbleShort')
        : t('calendar.meetupBubbleShort');
    } catch {
      return t('calendar.meetupBubbleShort');
    }
  }

  // 9.22.2 — alıntıya dokununca kaynak mesaja git ve kısa süre vurgula.
  // Kaynak bugün her zaman yüklü listede: geçmiş sayfalamasız dönüyor. 9.22.5
  // kararı (D-077) gereği sayfalama eklendiğinde eksik kaynak önce ayrıca
  // istenecek, sonra kaydırılacak — o yüzden burada sessizce çıkılıyor.
  function jumpToSource(sourceId: number) {
    const index = messagesNewestFirst.findIndex((m) => m.id === sourceId && isRealMessage(m));
    if (index < 0) return;
    listRef.current?.scrollToIndex({ index, animated: true, viewPosition: 0.5 });
    setHighlightId(sourceId);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 1400);
  }

  useEffect(() => () => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
  }, []);

  function onLongPressMessage(item: Message) {
    if (item.status === 'cancelled') return; // geri çekilmiş mesaja işlem yok
    setActionTarget(item);
  }

  async function togglePhotoLocation(item: Message) {
    const visible = !item.photo_location_visible;
    setActionTarget(null);
    const previous = item.photo_location_visible;
    setMessages((current) => current.map((message) =>
      message.id === item.id ? { ...message, photo_location_visible: visible } : message
    ));
    try {
      const updated = await api.updatePhotoLocationVisibility(token, item.id, visible);
      setMessages((current) => current.map((message) =>
        message.id === item.id ? { ...message, ...updated } : message
      ));
    } catch {
      setMessages((current) => current.map((message) =>
        message.id === item.id ? { ...message, photo_location_visible: previous } : message
      ));
      Alert.alert(t('common.error'), t('message.photoLocationUpdateError'));
    }
  }

  // Aynı emojiye tekrar basmak tepkiyi kaldırır; farklısına basmak değiştirir.
  // 9.21.8 — dokunuş yalnız O MESAJIN tepki dizisini yerinde günceller; eskiden
  // her dokunuştan sonra tüm konuşma yeniden çekiliyordu (uç nokta sayfalamasız
  // ve tepki toplamı mesaj başına ayrı alt sorgudan geliyor, yani maliyet
  // konuşma uzunluğuyla büyüyordu). İstek başarısız olursa dokunuştan önceki
  // dizi geri konur; 5 sn'lik yoklama ikinci güvence.
  async function toggleReaction(item: Message, emoji: string) {
    setActionTarget(null);
    const oncekiler = item.reactions;
    const kaldiriliyor = isTogglingOff(oncekiler, emoji);
    setMessages((prev) =>
      prev.map((m) => (m.id === item.id ? { ...m, reactions: applyReaction(m.reactions, emoji) } : m))
    );
    try {
      if (kaldiriliyor) {
        await api.removeReaction(token, item.id);
      } else {
        await api.reactToMessage(token, item.id, emoji);
      }
    } catch {
      setMessages((prev) => prev.map((m) => (m.id === item.id ? { ...m, reactions: oncekiler } : m)));
    }
  }

  // Web'de fiziksel klavide Enter mesajı gönderir, Shift+Enter yeni satır
  // ekler — masaüstü sohbet uygulamalarındaki standart davranış. Native'de
  // (iOS/Android) dokunmatik klavyenin "return" tuşu her zaman yeni satır
  // ekler; gönderme yalnızca "→" düğmesiyle olur (react-native-web'in kendi
  // TextInput'u multiline'da onSubmitEditing'i hiç tetiklemiyor, bu yüzden
  // web için ayrı bir onKeyPress gerekiyor).
  function handleKeyPress(e: any) {
    if (Platform.OS !== 'web') return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  /**
   * 3.10.5 — damgalı gönderim. Kullanıcıya GÖSTERİLEN tutar damga olarak gider;
   * kesilecek tutar ondan yüksekse sunucu 409 verir ve para hiç hareket etmez.
   * Farkı kullanıcıya gösterip onayını alırız, sonra yeni damgayla bir kez daha
   * deneriz. Düşüş gönderimi durdurmaz; güncel fiyat okunduğunda bilgi verilir.
   */
  async function sendStamped(
    attempt: (stamp: { amount: number; currency: string } | null) => Promise<Message>
  ): Promise<Message | null> {
    return sendStampedWith(null, attempt);
  }

  /**
   * 9.1.6 — ses mesajı mesaj başına değil DAKİKA başına ücretlendiği için
   * damgası da mesaj fiyatından gelemez: taksimetrenin gösterdiği tutarla
   * mühürlenir. `override` verilmezse mesaj fiyatı kullanılır.
   */
  async function sendStampedWith(
    override: { amount: number; currency: string } | null,
    attempt: (stamp: { amount: number; currency: string } | null) => Promise<Message>
  ): Promise<Message | null> {
    const quote = messagePriceLimitRef.current;
    // If the initial quote failed to load, never submit an unbounded charge.
    const stamp = override ?? (quote ? { amount: quote.amount, currency: quote.currency }
      : { amount: 0, currency: (await api.getMessagePrice(token, otherId)).currency });
    try {
      return await attempt(stamp);
    } catch (err) {
      const current = err instanceof ApiError && err.status === 409 && err.code === 'price_changed'
        ? err.details?.current
        : null;
      if (!current || typeof current.amount !== 'number' || typeof current.currency !== 'string') {
        throw err;
      }
      const confirmed = await new Promise<boolean>((resolve) => {
        Alert.alert(
          t('message.priceChangedTitle'),
          t('message.priceChangedBody', {
            old: formatMoney(Number(stamp?.amount ?? 0), stamp?.currency, targetLang),
            new: formatMoney(current.amount, current.currency, targetLang),
          }),
          [
            { text: t('common.cancel'), style: 'cancel', onPress: () => resolve(false) },
            { text: t('message.priceChangedSend'), onPress: () => resolve(true) },
          ],
          { cancelable: false }
        );
      });
      if (!confirmed) return null;
      applyMessagePrice({
        ...(quote ?? { seller_amount: current.amount, seller_currency: current.currency, personal: false }),
        amount: current.amount,
        currency: current.currency,
      }, true);
      return await attempt({ amount: current.amount, currency: current.currency });
    }
  }

  /**
   * Mesajdaki bağlantıyı açar — ama önce NEREYE gittiğini sorar.
   *
   * Uygulamanın diğer `Linking.openURL` çağrıları hep kendi ürettiğimiz adresleri
   * açıyor; bu, kullanıcının yazdığı adresi açan tek yer. Ücretli mesajlaşmada
   * karşı taraf tanımadığın biri olabildiği ve platform dışına çekmek için
   * gerçek bir güdü bulunduğu için hedef host onaydan önce gösteriliyor: kimlik
   * avının en yaygın biçimi, tanıdık adı alt alan adına gizleyen adrestir ve
   * uzun bir bağlantıda gözden kaçar.
   */
  async function openMessageLink(url: string) {
    const confirmed = await new Promise<boolean>((resolve) => {
      Alert.alert(
        t('message.linkOpenTitle'),
        t('message.linkOpenBody', { host: linkHost(url) }),
        [
          { text: t('common.cancel'), style: 'cancel', onPress: () => resolve(false) },
          { text: t('message.linkOpenConfirm'), onPress: () => resolve(true) },
        ],
        { cancelable: true, onDismiss: () => resolve(false) }
      );
    });
    if (!confirmed) return;
    try {
      await Linking.openURL(url);
    } catch {
      Alert.alert(t('common.error'), t('message.linkOpenFailed'));
    }
  }

  /** Bu mesaj için varsayılandan farklı bir görünüm seç; yine dokununca varsayılana döner. */
  function toggleLinkOverride(view: 'plain' | LinkView) {
    setLinkPreviewFailed(false);
    setLinkOverride((current) => (current === view ? null : view));
  }

  /** Gönderim düşerse seçilen görünüm ve hazır önizleme geri gelir. */
  function restoreLinkChoice(choice: LinkPreview | null) {
    if (!choice) return;
    setLinkPreview(choice);
    setLinkOverride(choice.view === linkPrefs?.view ? null : choice.view);
  }

  async function send() {
    if (!text.trim()) return;
    setSending(true);
    const draft = text.trim();
    const replyTarget = replyingTo;
    // 9.32 / D-653 — seçilen önizleme (varsa) mesajın şifreli içeriğine girer.
    const linkChoice = linkView !== 'plain' && linkPreview && linkPreview.url === draftLink && !linkPreviewLoading
      ? { ...linkPreview, view: linkView }
      : null;
    const plaintext = encodeMessagePayload(draft, linkChoice);
    setText('');
    setReplyingTo(null);
    setLinkOverride(null);
    setLinkPreview(null);
    try {
      // 9.34 / D-610 — karşı taraf anahtar yayımlamışsa mesaj CİHAZDA şifrelenir
      // ve sunucuya zarf gider. Anahtarı yoksa düz metin; geri düşüş yalnız o
      // durumda (D-611), başka hata sessizce şifrelemeyi düşürmez.
      // 9.34 / D-627 — mesaj, okuyabilmesi gereken HER cihaz için ayrı
      // şifreleniyor: karşı tarafın cihazları ve kendi diğer cihazlarım.
      const sealedRaw = await encryptIfPossible(token, otherId, plaintext, myId);
      setEncryption(sealedRaw.type === 'encrypted' ? 'on' : sealedRaw.reason ?? 'peer');
      // Karşı tarafın anahtarı yoksa mesaj düz metin gider (D-610). İçerik o
      // zaman yapı değil, METİN olur: bildirim, sohbet listesi ve çeviri metni
      // okur. 9.32 / D-658 — önizleme ise metinden AYRI bir alanda gider; bu
      // konuşmada sunucu linki zaten görüyor (Batuhan: "Evet, gitsin").
      const sealed = sealedRaw.type === 'text' ? { ...sealedRaw, content: draft } : sealedRaw;
      const openLink = sealed.type === 'text' && linkChoice ? cleanLinkPreview(linkChoice, draft) : null;
      const msg = await sendStamped((stamp) =>
        api.sendMessage(token, otherId, sealed.content, sealed.type, replyTarget?.id, undefined,
          stamp, undefined, sealed.envelopes, openLink));
      if (!msg) {
        // Kullanıcı yeni fiyatı onaylamadı: taslak ve yanıt hedefi geri gelsin.
        setText(draft);
        setReplyingTo(replyTarget);
        restoreLinkChoice(linkChoice);
        return;
      }
      // Gönderen kendi şifreli mesajını ÇÖZEMEZ (gönderme zinciri ayrıdır), bu
      // yüzden düz metin hemen önbelleğe yazılır — yoksa kişi yeniden açtığında
      // kendi yazdığını göremezdi (D-611).
      if (sealed.type === 'encrypted') {
        await rememberOutgoing(msg.id, plaintext);
      }
      // Ekrana düz metni koy: elimizde zaten var, çözmeye çalışmaya gerek yok.
      const shownLink = sealed.type === 'encrypted' ? decodeMessagePayload(plaintext).link : openLink;
      setMessages((prev) => upsertLocal(
        prev, sealed.type === 'encrypted'
          ? { ...msg, content: draft, type: 'text', link_preview: shownLink ?? null }
          : { ...msg, link_preview: shownLink ?? null }));
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    } catch (err) {
      setText(draft);
      setReplyingTo(replyTarget);
      restoreLinkChoice(linkChoice);
      if (err instanceof NetworkError) {
        Alert.alert(t('message.noConnection'), t('message.checkInternet'));
      } else if (err instanceof ApiError && err.status === 402) {
        Alert.alert(t('message.lowBalanceTitle'), t('message.lowBalanceBody'));
      } else {
        // Server/runtime messages are not UI translations (and may not be Errors).
        Alert.alert(t('common.error'), t('message.sendError'));
      }
    } finally {
      setSending(false);
    }
  }

  // Sohbet başı ücret özeti. Kapsam bilinçli olarak MESAJ ve ARAMA ile sınırlı:
  // ikisi de bu ekrandan hemen yapılabilen, ödemesi anında gerçekleşen eylemler
  // ve ikisi de ödeyenin kendi para biriminde gösterilebiliyor. Buluşma ve
  // rezervasyon ücreti teklif anında donar ve satıcının para biriminde gösterilir
  // (D-291); onu buraya farklı bir birimle koymak tek listede iki para birimi
  // demek olurdu.
  // 9.34 / D-661, D-664 — şifresiz konuşma, sohbetin EN BAŞINDA söylenir (ters
  // listede altbilgi = konuşmanın tepesi), fiyat notunun üstünde. Batuhan:
  // "Direkt başlığın altında değil de; mesaj kutusunda ilk zamanlarda
  // belirtilse yeterli … Sürekli göz önünde bulunmasına gerek yok." Neden
  // ⓘ'nin arkasında: resmî hesap, karşı tarafın eski uygulaması ya da bu cihaz.
  const encryptionNotice = encryption === 'peer' || encryption === 'self' ? (
    <View style={styles.encryptionNotice} testID="unencrypted-notice">
      {encryption === 'self' ? (
        <LabelWithInfo
          label={t('message.unencrypted')}
          info={t('message.unencryptedSelfInfo')}
          labelStyle={styles.unencryptedText}
          testID="unencrypted"
        />
      ) : otherIsOfficial ? (
        <LabelWithInfo
          label={t('message.unencrypted')}
          info={t('message.unencryptedOfficialInfo')}
          labelStyle={styles.unencryptedText}
          testID="unencrypted"
        />
      ) : (
        <LabelWithInfo
          label={t('message.unencrypted')}
          info={t('message.unencryptedPeerInfo')}
          labelStyle={styles.unencryptedText}
          testID="unencrypted"
        />
      )}
    </View>
  ) : null;

  const priceNotice = messagePrice ? (
    <View style={styles.conversationPriceNotice} testID="conversation-price-notice">
      <Text style={styles.priceNoticeHeading}>{t('message.priceNoticeTitle')}</Text>
      <Text style={styles.priceNoticeLine}>
        {t('message.priceNoticeMessage', {
          amount: Number(messagePrice.amount) === 0
            ? t('message.priceChange.free')
            : formatMoney(Number(messagePrice.amount), messagePrice.currency, targetLang),
        })}
      </Text>
      {callsEnabled && mediaAvailable && callPrice && (
        <Text style={styles.priceNoticeLine}>
          {t('message.priceNoticeCall', {
            amount: Number(callPrice.amount) === 0
              ? t('message.priceChange.free')
              : formatPerMinute(Number(callPrice.amount), callPrice.currency, targetLang, t('calendar.minutesShort')),
          })}
        </Text>
      )}
      {(messagePrice.personal || callPrice?.personal) && (
        <Text style={styles.priceNoticePersonal}>{t('message.priceNoticePersonal')}</Text>
      )}
    </View>
  ) : null;

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={insets.bottom}>
      <ScreenHeader contentStyle={styles.header}>
        <HeaderBackButton onPress={onBack} />
        <TouchableOpacity style={styles.headerCenter} onPress={onViewProfile} activeOpacity={0.7}>
          <Avatar photoUrl={otherPhoto} name={otherName} size={28} style={styles.headerAvatar} tint={moodTint(peerMood)} />
          <View style={styles.headerIdentity}>
            <View style={styles.headerNameRow}>
              <Text style={styles.headerName} numberOfLines={1}>{otherName}</Text>
              {otherIsOfficial && <VerifiedBadge size={15} />}
            </View>
            {otherLastSeen && (
              <Text style={styles.headerLastSeen} numberOfLines={1}>{timeAgo(otherLastSeen, t, targetLang)}</Text>
            )}
          </View>
        </TouchableOpacity>
        <View style={styles.headerRight}>
          {/* Aramayı başlatma yüzeyi (9.4.12.1): iki taraf zaten konuşuyor,
              yani 9.4.7'nin "önce mesajlaşmış olma" kapısı kendiliğinden
              sağlanıyor. Düğme yalnızca arama açıkken görünür — ses taşınmadan
              ücret işletmemek için giriş noktası kapalı (calls/config.ts). */}
          {/* Bayrak build zamanında, modül ise ikilide: ikisi ayrışabildiği için
              çalışma zamanında da bakılıyor (9.4.4.3.1). Modül yoksa buton hiç
              çizilmez — basılınca çökmesindense görünmemesi doğru. */}
          {callsEnabled && mediaAvailable && onStartCall && (
            <GlassControlButton
              onPress={() => setCallActionMenu(true)}
              accessibilityLabel={t('call.title')}>
              <Ionicons name="call-outline" size={22} color={colors.black} />
            </GlassControlButton>
          )}
        </View>
      </ScreenHeader>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.black} />
        </View>
      ) : (
        <FlatList
          ref={listRef}
          data={messagesNewestFirst}
          inverted
          maintainVisibleContentPosition={{ minIndexForVisible: 0, autoscrollToTopThreshold: 48 }}
          keyExtractor={(item) => `${item.type === 'call' ? 'c' : item.type === 'identity_change' ? 'k' : 'm'}${item.id}`}
          contentContainerStyle={styles.list}
          // 3.10.5 — sohbet başı fiyat uyarısı. Liste ters çalıştığı için
          // "footer" görsel olarak sohbetin EN BAŞIDIR. Bir kez gösterilip
          // kaybolmaz: burada kalıcı olarak durur ve geriye dönüldüğünde hep
          // bulunur. D-364 ile gönderim çubuğundaki sürekli fiyat satırı kaldırıldı;
          // artış gönderimde onay, algılanan düşüş tek seferlik bilgi gerektirir.
          ListFooterComponent={<>{encryptionNotice}{priceNotice}</>}
          // Baloncuk yükseklikleri değişken olduğu için henüz ölçülmemiş bir
          // satıra scrollToIndex doğrudan atlayamaz (hata fırlatır). Yaklaşık
          // ofsete gidip ölçümün oturmasını bekledikten sonra tekrar deniyoruz.
          onScrollToIndexFailed={(info) => {
            listRef.current?.scrollToOffset({
              offset: info.averageItemLength * info.index,
              animated: false,
            });
            setTimeout(() => {
              listRef.current?.scrollToIndex({ index: info.index, animated: true, viewPosition: 0.5 });
            }, 120);
          }}
          renderItem={({ item, index }: { item: Message; index: number }) => {
            const itemDayTop = new Date(item.created_at).toDateString();
            const olderDayTop = index < messagesNewestFirst.length - 1
              ? new Date(messagesNewestFirst[index + 1].created_at).toDateString()
              : null;
            const showDateSepTop = itemDayTop !== olderDayTop;

            // 9.34 / D-634 — karşı tarafın anahtarı değişti. Satır sohbetin
            // içinde ve OLDUĞU ANDA duruyor; tepede kalıcı bir şerit "ne zaman,
            // hangi mesajlardan sonra" sorusunu yanıtlamazdı. Kime ait olduğu
            // yok: bu bir mesaj değil, sohbete dair bir olay.
            // 9.26 / D-638 — resmî hesabın "bildirimleri aç" kartı. İçerik düz
            // metin; düğmeyi kart ekliyor. Kart bir MESAJ: tepki ve alıntı
            // yollarına kapalı tutulması gerekmiyor ama para yolu yok, o yüzden
            // uzun basma menüsü de açılmıyor.
            if (item.type === 'notification_offer') {
              return (
                <View>
                  {showDateSepTop && (
                    <Text style={styles.dateSep}>{dateLabel(item.created_at, t, targetLang)}</Text>
                  )}
                  <View style={styles.rowOther}>
                    <NotificationOfferCard
                      token={token}
                      text={item.content}
                      time={formatTime(item.created_at, targetLang)}
                    />
                  </View>
                </View>
              );
            }

            if (item.type === 'identity_change') {
              return (
                <View>
                  {showDateSepTop && (
                    <Text style={styles.dateSep}>{dateLabel(item.created_at, t, targetLang)}</Text>
                  )}
                  <Text style={styles.identityChangeLine} testID="identity-change-row">
                    {t('message.identityChanged')}
                  </Text>
                </View>
              );
            }

            const mine = item.sender_id === myId;
            const isCancelled = item.status === 'cancelled';
            const isExpired = item.status === 'expired';
            const isRead = item.status === 'replied' || !!item.read_at;
            const quoted = item.reply_to_id
              ? messages.find((m) => m.id === item.reply_to_id && isRealMessage(m))
              : null;
            // Buluşma teklifi balonu — content, oluşturma anındaki durağan bir
            // JSON anlık görüntüsü (kabul/red/iptal sonradan olsa da metin
            // güncellenmez, kesin durum Takvim sekmesinde). Bozuk/parse
            // edilemeyen içerikte normal metin balonuna düşer.
            // Çağrı olayı bir mesaj DEĞİL: sunucuda `calls` tablosundan okuma
            // anında üretiliyor. Bu yüzden tepki/yanıt/alıntı yolları ona
            // kapatılmalı — id'ler ayrı dizilerden geldiği için bir çağrının
            // id'si pekâlâ bir mesajın id'siyle çakışabilir ve tepki yanlış
            // satıra yazılırdı.
            const isCall = item.type === 'call';
            const isImage = item.type === 'image' && !isCancelled;
            const isVideo = item.type === 'video' && !isCancelled;
            const isVoice = item.type === 'voice' && !isCancelled;
            const siparisKart = item.type === 'voice_order' ? parseVoiceOrderCard(item.content) : null;
            const konumKart = item.type === 'location' && !isCancelled ? parseLocationCard(item.content) : null;
            const canliKart = item.type === 'location_share' ? parseLiveShareCard(item.content) : null;
            // Balonu saydam ve dolgusuz yapan her karar medyanın TAMAMINA ait,
            // fotoğrafa değil: alıntı kutusu, vurgu ve saat rengi "koyu balon
            // üstündeyim" varsayımıyla çiziliyor ve medyada koyu balon yok.
            // Tek kavram, bir sonraki medya türünde bu beş yeri tekrar
            // aramayı gereksiz kılıyor.
            const isMedia = isImage || isVideo;
            const callData = isCall
              ? (() => { try { return JSON.parse(item.content); } catch { return null; } })()
              : null;
            const meetupData = item.type === 'meetup'
              ? (() => { try { return JSON.parse(item.content); } catch { return null; } })()
              : null;
            const displayedText = isCancelled
              ? t('message.withdrawn')
              // 9.34 / D-612 — buraya `encrypted` türüyle gelen bir mesaj,
              // ÇÖZÜLEMEMİŞ olandır: çözülenler `text`e dönüştürülüyor
              // (e2ee/messages.ts). İçeriği zarf olduğu için ham yazılamaz;
              // boş balon çizmek de yalan olurdu. Dürüst karşılık bu etiket.
              : item.type === 'encrypted'
                ? t('message.encrypted')
                : translateOn && translations[item.id]
                  ? translations[item.id]
                  : item.content;
            const textBubbleMeasurement = textBubbleMeasurements[item.id];
            const isEmojiOnly = item.type === 'text' && !isCancelled && !isExpired
              && isEmojiOnlyMessage(displayedText);
            // 9.32 / D-653 — gönderen önizleme seçtiyse kısa görünüm ya da büyük
            // kart linkin YERİNE geçer; metnin geri kalanı olduğu gibi kalır.
            const bubbleLink = item.type === 'text' && !isCancelled && !isExpired && item.link_preview
              ? item.link_preview
              : null;
            const bubbleText = bubbleLink ? withoutLink(displayedText, bubbleLink.url) : displayedText;

            const itemDay = new Date(item.created_at).toDateString();
            // Ters dizide bir sonraki kayıt kronolojik olarak daha eskidir.
            // Gün etiketi, görsel akışta o günün ilk (en eski) mesajında kalır.
            const olderDay = index < messagesNewestFirst.length - 1
              ? new Date(messagesNewestFirst[index + 1].created_at).toDateString()
              : null;
            const showDateSep = itemDay !== olderDay;

            return (
              <View>
                {showDateSep && (
                  <Text style={styles.dateSep}>{dateLabel(item.created_at, t, targetLang)}</Text>
                )}
              <SwipeToReply enabled={!isCancelled && !isCall} onReply={() => setReplyingTo(item)}>
              <View style={mine ? styles.rowMine : styles.rowOther}>
                <TouchableOpacity
                  activeOpacity={isCall ? 1 : 0.8}
                  onLongPress={isCall ? undefined : () => onLongPressMessage(item)}
                  testID={isCall
                    ? `call-message-${mine ? 'mine' : 'other'}`
                    : item.type === 'text'
                      ? `text-message-${mine ? 'mine' : 'other'}-${item.id}`
                      : undefined}
                  style={[
                    styles.bubble,
                    isMedia
                      ? styles.imageBubble
                      : isCall
                        ? [
                            styles.callBubble,
                            mine ? styles.callBubbleMine : styles.callBubbleOther,
                          ]
                      : mine
                        ? styles.bubbleMine
                        : styles.bubbleOther,
                    isCancelled && styles.bubbleCancelled,
                    isEmojiOnly && styles.emojiBubble,
                    highlightId === item.id && !isCall && !isMedia
                      && (isEmojiOnly ? styles.emojiHighlight : styles.bubbleHighlight),
                    item.type === 'text'
                      && !isEmojiOnly
                      && !quoted
                      && !isExpired
                      && !bubbleLink
                      && textBubbleMeasurement?.content === displayedText
                      ? { width: textBubbleMeasurement.width }
                      : undefined,
                  ]}>
                  {quoted && (
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => jumpToSource(quoted.id)}
                      onLongPress={() => onLongPressMessage(item)}
                      style={[styles.quoteBox, mine && !isMedia && !isEmojiOnly && styles.quoteBoxMine]}>
                      <Text style={[styles.quoteAuthor, mine && !isMedia && !isEmojiOnly && styles.quoteTextMine]} numberOfLines={1}>
                        {quoted.sender_id === myId ? t('message.you') : otherName}
                      </Text>
                      <Text style={[styles.quotePreview, mine && !isMedia && !isEmojiOnly && styles.quoteTextMine]} numberOfLines={1}>
                        {quoted.status === 'cancelled'
                          ? t('message.withdrawn')
                          : quoted.type === 'image'
                          ? t('message.photo')
                          : quoted.type === 'meetup'
                          ? reservationPreview(quoted.content)
                          : translateOn && translations[quoted.id]
                          ? translations[quoted.id]
                          : quoted.content}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {isCall && callData ? (
                    <View style={styles.callCard}>
                      <View style={[
                        styles.callIcon,
                        mine && styles.callIconMine,
                        isMissedCallForViewer(callData, mine) && styles.callIconMissed,
                      ]}>
                        <Ionicons
                          name={isMissedCallForViewer(callData, mine) ? 'call-outline' : 'call'}
                          size={18}
                          color={isMissedCallForViewer(callData, mine)
                            ? '#D94A45'
                            : mine
                              ? colors.white
                              : colors.black}
                        />
                      </View>
                      <View
                        testID={`call-message-copy-${mine ? 'mine' : 'other'}`}
                        style={styles.callCardCopy}>
                        <View style={styles.callTitleRow}>
                          <Ionicons
                            name={mine ? 'arrow-up' : 'arrow-down'}
                            size={12}
                            style={styles.callDirArrow}
                            color={isMissedCallForViewer(callData, mine)
                              ? '#D94A45'
                              : mine
                                ? colors.white
                                : colors.black}
                          />
                          <Text
                            testID={`call-message-title-${mine ? 'mine' : 'other'}`}
                            numberOfLines={2}
                            ellipsizeMode="tail"
                            style={[
                              styles.callCardTitle,
                              mine && styles.callCardTitleMine,
                              isMissedCallForViewer(callData, mine) && styles.callCardTitleMissed,
                            ]}>
                            {callData.status === 'ended'
                              ? t(callData.kind === 'video' ? 'call.logAnsweredVideo' : 'call.logAnswered')
                              : callLabel(callData, mine, t)}
                          </Text>
                        </View>
                        {callData.status === 'ended' && callData.seconds > 0 && (
                          <Text style={[styles.callCardDetail, mine && styles.callCardDetailMine]}>
                            {callDuration(callData.seconds)}
                          </Text>
                        )}
                      </View>
                    </View>
                  ) : isVideo ? (
                    <MessageVideo
                      url={mediaUrl(item.content)}
                      mine={mine}
                      durationSeconds={item.media_duration_seconds}
                      onLongPress={() => onLongPressMessage(item)}
                    />
                  ) : canliKart ? (
                    <MessageLiveLocation
                      state={item.location_share ?? null}
                      shareId={canliKart.shareId}
                      mine={mine}
                      onOpen={setWatchedShareId}
                    />
                  ) : konumKart ? (
                    <MessageLocation
                      card={konumKart}
                      mine={mine}
                      onLongPress={() => onLongPressMessage(item)}
                    />
                  ) : siparisKart ? (
                    <MessageVoiceOrder
                      card={siparisKart}
                      state={item.voice_order ?? null}
                      mine={mine}
                      language={targetLang}
                      onLongPress={() => onLongPressMessage(item)}
                      busy={orderBusy}
                      onRecord={() => openDeliveryRecorder(siparisKart.orderId, siparisKart.seconds)}
                      onDecline={() => confirmDecline(siparisKart.orderId)}
                    />
                  ) : isVoice ? (
                    <MessageVoice
                      url={mediaUrl(item.content)}
                      mine={mine}
                      durationSeconds={item.media_duration_seconds}
                      onLongPress={() => onLongPressMessage(item)}
                    />
                  ) : isImage ? (
                    <>
                      <MessageImage
                        url={mediaUrl(item.content)}
                        onLongPress={() => onLongPressMessage(item)}
                        mine={mine}
                      />
                      {item.photo_location_visible && item.photo_location_city && (
                        <View style={styles.photoLocation}>
                          <Text style={styles.photoLocationPlace}>
                            {[item.photo_location_district, item.photo_location_city]
                              .filter((value, position, values) => value && values.indexOf(value) === position)
                              .join(' · ')}
                          </Text>
                          <Text style={styles.photoLocationSource}>
                            {t(item.photo_location_source === 'capture_location'
                              ? 'message.photoLocationFromCapture'
                              : 'message.photoLocationFromFile')}
                          </Text>
                        </View>
                      )}
                    </>
                  ) : item.type === 'meetup' && meetupData ? (
                    <View style={styles.meetupCard}>
                      <View style={styles.meetupCardTitleRow}>
                        {meetupData.kind !== 'call' && (
                          <CalendarIcon active color={mine ? colors.white : colors.black} />
                        )}
                        <Text style={[styles.meetupCardTitle, mine && styles.bubbleTextMine]}>
                          {t(meetupData.changeType === 'reschedule'
                            ? meetupData.kind === 'call'
                              ? 'call.rescheduleBubble'
                              : 'calendar.rescheduleBubble'
                            : meetupData.kind === 'call'
                              ? 'call.reservationBubble'
                              : 'calendar.meetupBubble')
                            .replace(/^📅\s*/u, '')}
                        </Text>
                      </View>
                      <Text style={[styles.meetupCardWhen, mine && styles.bubbleTextMine]}>
                        {formatNosubjectCalendarEventWhen({
                          proposedAt: meetupData.proposedAt,
                          durationMinutes: meetupData.durationMinutes ?? 1,
                          sourceTimeZone: meetupData.sourceTimeZone,
                        }, targetLang, getDeviceTimeZone() ?? 'UTC')}
                      </Text>
                      {!!meetupData.location && (
                        <Text style={[styles.meetupCardWhen, mine && styles.bubbleTextMine]}>{meetupData.location}</Text>
                      )}
                      {typeof meetupData.totalCost === 'number' && (
                        <Text style={[styles.meetupCardWhen, mine && styles.bubbleTextMine]}>
                          {typeof meetupData.durationMinutes === 'number'
                            ? formatDisplayCount(meetupData.durationMinutes, targetLang)
                            : meetupData.durationMinutes} {t('calendar.minutesShort')} · {formatMoney(meetupData.totalCost, meetupData.currency, targetLang)}
                        </Text>
                      )}
                    </View>
                  ) : (
                    <>
                    {bubbleLink?.view === 'card' && (
                      <LinkCard
                        preview={bubbleLink}
                        mine={mine}
                        testID={`message-link-card-${item.id}`}
                        onPress={() => openMessageLink(bubbleLink.url)}
                        onLongPress={() => onLongPressMessage(item)}
                      />
                    )}
                    {(!bubbleLink || bubbleText.length > 0) && (
                    <Text
                      testID={item.type === 'text' ? `text-message-copy-${item.id}` : undefined}
                      onTextLayout={item.type === 'text' && !quoted && !isExpired && !isEmojiOnly && !bubbleLink
                        ? (event) => {
                            const widestLine = Math.max(
                              0,
                              ...event.nativeEvent.lines.map((line) => line.width)
                            );
                            if (!Number.isFinite(widestLine) || widestLine <= 0) return;

                            // Yoga, uzun bir metni iki satıra böldüğünde balonu
                            // azami genişlikte bırakabiliyor. Gerçek satır genişliği
                            // ile saat/okundu alanından büyük olanı kullanarak balonu
                            // tekrar içeriğe topluyoruz; iki piksellik pay yeni ölçümde
                            // aynı kelimenin bir alt satıra düşmesini engelliyor.
                            const nextWidth = Math.ceil(
                              Math.max(widestLine, TEXT_BUBBLE_META_WIDTH)
                              + spacing.md * 2
                              + TEXT_BUBBLE_WIDTH_GUARD
                            );
                            setTextBubbleMeasurements((current) => {
                              const previous = current[item.id];
                              return previous?.content === displayedText
                                && Math.abs(previous.width - nextWidth) <= 1
                                ? current
                                : {
                                    ...current,
                                    [item.id]: { content: displayedText, width: nextWidth },
                                  };
                            });
                          }
                        : undefined}
                      style={[styles.bubbleText, mine && !isCancelled && !isEmojiOnly && styles.bubbleTextMine,
                        isEmojiOnly && styles.emojiMessageText]}>
                      {/* Bağlantı yalnız gerçek metin mesajında aranır: geri
                          çekilmiş/süresi dolmuş balonun içeriği bizim kendi
                          etiketimizdir, orada adres aramak anlamsız olurdu. */}
                      {item.type === 'text' && !isCancelled && !isExpired
                        ? splitMessageLinks(bubbleText).map((segment, segmentIndex) => (
                            segment.type === 'link' ? (
                              <Text
                                key={`${segmentIndex}-${segment.url}`}
                                style={styles.messageLink}
                                testID={`message-link-${item.id}`}
                                accessibilityRole="link"
                                onPress={() => openMessageLink(segment.url)}
                                onLongPress={() => onLongPressMessage(item)}
                                suppressHighlighting>
                                {segment.value}
                              </Text>
                            ) : (
                              <Text key={`${segmentIndex}-t`}>{segment.value}</Text>
                            )
                          ))
                        : displayedText}
                    </Text>
                    )}
                    {bubbleLink?.view === 'compact' && (
                      <LinkPill
                        preview={bubbleLink}
                        mine={mine}
                        testID={`message-link-pill-${item.id}`}
                        onPress={() => openMessageLink(bubbleLink.url)}
                        onLongPress={() => onLongPressMessage(item)}
                      />
                    )}
                    </>
                  )}
                  <Text testID={`message-time-${item.type}-${item.id}`}
                    style={[styles.msgTime, mine && !isCancelled && !isMedia && !isEmojiOnly && styles.msgTimeMine,
                      isEmojiOnly && styles.emojiMessageTime]}>
                    {formatTime(item.created_at, targetLang)}
                    {/* 9.2 / D-650 — web'den gönderildi. Bir insanın yazdığını hiçbir
                        şey kanıtlayamaz; tarayıcıyı süren bir yapay zekâ ajanı en
                        kolay web'den çalışır, alıcı en azından nereden geldiğini görür. */}
                    {item.sent_via === 'web' && (
                      <Text testID={`message-sent-via-web-${item.id}`}>{`  · ${t('message.sentFromWeb')}`}</Text>
                    )}
                    {mine && !isCall && !isCancelled && !isExpired && (
                      <Text style={isRead && styles.tickRead}>{'  ✓✓'}</Text>
                    )}
                    {isExpired && (
                      <>
                        {'  · '}
                        {t('message.expired')}
                      </>
                    )}
                  </Text>
                </TouchableOpacity>
                {item.reactions && item.reactions.length > 0 && (
                  // 9.21.9 / D-642 — Batuhan'ın gönderdiği örnekteki gibi: balonun
                  // alt kenarına binen TEK bir hap, emojiler yan yana. Sayı her
                  // emojinin ALTINDA ve çok küçük (Batuhan: "sayı her smiley'in
                  // altında çok çok küçük bir şekilde belirtilsin"). Ayrı ayrı
                  // çipler balonun altında ikinci bir satır gibi duruyor ve
                  // mesajdan kopuk görünüyordu.
                  <View
                    testID="reaction-pill"
                    style={[styles.reactionPill, mine ? styles.reactionPillMine : styles.reactionPillOther]}>
                    {item.reactions.map((r) => (
                      <TouchableOpacity
                        key={r.emoji}
                        testID={`reaction-${r.emoji}`}
                        style={[styles.reactionItem, r.mine && styles.reactionItemMine]}
                        accessibilityRole="button"
                        // Emoji ve küçük sayı ekran okuyucuda "kaç kişi verdi,
                        // biri sen misin" bilgisini taşımıyor; etiket taşıyor.
                        accessibilityLabel={t(r.mine ? 'message.reactionA11yMine' : 'message.reactionA11y', {
                          emoji: r.emoji,
                          count: r.count,
                        })}
                        onPress={() => toggleReaction(item, r.emoji)}>
                        <Text style={styles.reactionEmoji}>{r.emoji}</Text>
                        <Text
                          style={[styles.reactionCount, r.mine && styles.reactionCountMine]}
                          testID={`reaction-count-${r.emoji}`}>
                          {formatDisplayCount(r.count, targetLang)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
                {mine && item.id === lastSeenMessageId && item.read_at && (
                  <Text style={styles.seenAt} testID="seen-at">
                    {t('message.seenAt', { time: formatTime(item.read_at, targetLang) })}
                  </Text>
                )}
              </View>
              </SwipeToReply>
              </View>
            );
          }}
        />
      )}

      {otherIsTyping && (
        <View style={styles.typingRow}>
          <View style={styles.typingBubble}>
            <Text style={styles.typingText}>···</Text>
          </View>
        </View>
      )}

      {replyingTo && (
        <View style={styles.replyBar}>
          <View style={styles.replyBarLine} />
          <View style={styles.replyBarContent}>
            <Text style={styles.replyBarAuthor} numberOfLines={1}>
              {replyingTo.sender_id === myId ? t('message.you') : otherName}
            </Text>
            <Text style={styles.replyBarPreview} numberOfLines={1}>
              {replyingTo.type === 'image' ? t('message.photo') : replyingTo.type === 'meetup' ? reservationPreview(replyingTo.content) : replyingTo.content}
            </Text>
          </View>
          <TouchableOpacity
            onPress={() => setReplyingTo(null)}
            accessibilityRole="button"
            accessibilityLabel={t('common.cancel')}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={styles.replyBarClose}>✕</Text>
          </TouchableOpacity>
        </View>
      )}
      <Modal
        visible={!!actionTarget}
        transparent
        animationType="fade"
        onRequestClose={() => setActionTarget(null)}>
        <TouchableOpacity
          style={styles.sheetOverlay}
          activeOpacity={1}
          onPress={() => setActionTarget(null)}>
          <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
            <View style={styles.emojiRow}>
              {REACTION_EMOJIS.map((e) => {
                const active = actionTarget?.reactions?.some((r) => r.mine && r.emoji === e);
                return (
                  <TouchableOpacity
                    key={e}
                    style={[styles.emojiBtn, active && styles.emojiBtnActive]}
                    accessibilityRole="button"
                    accessibilityLabel={t(active ? 'message.reactionRemove' : 'message.reactionAdd', { emoji: e })}
                    onPress={() => { if (actionTarget) toggleReaction(actionTarget, e); }}>
                    <Text style={styles.emojiText}>{e}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <TouchableOpacity
              style={styles.sheetAction}
              accessibilityRole="button"
              accessibilityLabel={t('message.reply')}
              onPress={() => {
                const target = actionTarget;
                setActionTarget(null);
                if (target) setReplyingTo(target);
              }}>
              <Text style={styles.sheetActionText}>{t('message.reply')}</Text>
            </TouchableOpacity>
            {actionTarget?.type === 'image' &&
              actionTarget.sender_id === myId &&
              (actionTarget.photo_location_source === 'file_metadata' ||
                actionTarget.photo_location_source === 'capture_location') && (
                <TouchableOpacity
                  style={styles.sheetAction}
                  onPress={() => togglePhotoLocation(actionTarget)}>
                  <Text style={styles.sheetActionText}>
                    {t(actionTarget.photo_location_visible
                      ? 'message.photoLocationHideAfter'
                      : 'message.photoLocationShowAfter')}
                  </Text>
                </TouchableOpacity>
              )}
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={callActionMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setCallActionMenu(false)}>
        <TouchableOpacity
          style={styles.sheetOverlay}
          accessible={false}
          activeOpacity={1}
          onPress={() => setCallActionMenu(false)}>
          <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
            <TouchableOpacity
              style={styles.sheetAction}
              testID="call-now-action"
              onPress={() => {
                setCallActionMenu(false);
                // 3.10.5 — gösterilen dakika ücreti damga olarak gider. Ücret
                // yüklenemediyse damga da gitmez: görülmemiş bir sayı damgalanmaz.
                const quote = callPriceRef.current;
                onStartCall?.(
                  undefined,
                  quote ? { amount: quote.amount, currency: quote.currency } : null
                );
              }}>
              <Text style={styles.sheetActionText}>{t('call.callNow')}</Text>
              {callPrice && (
                <Text style={styles.sheetActionNote} testID="call-now-price">
                  {Number(callPrice.amount) === 0
                    ? t('call.priceFree')
                    : formatPerMinute(Number(callPrice.amount), callPrice.currency, targetLang, t('calendar.minutesShort'))}
                </Text>
              )}
            </TouchableOpacity>
            {/* 9.4.14 — görüntülü arama kendi satırı, kendi ücreti. Sesli
                aramanın içine bir anahtar olarak değil ayrı bir seçenek olarak
                konuyor: iki kipin ücreti ayrı olduğu için kullanıcı hangi
                ücreti kabul ettiğini basmadan önce görmeli. */}
            {videoCallsSupported && (
              <TouchableOpacity
                style={styles.sheetAction}
                testID="call-video-action"
                onPress={() => {
                  setCallActionMenu(false);
                  // Damga, basılan satırın gösterdiği ücrettir: görüntülü
                  // satırda görüntülü kota damgalanır. Ücret yüklenemediyse
                  // damga da gitmez — görülmemiş bir sayı damgalanmaz.
                  const quote = videoCallPriceRef.current;
                  onStartCall?.(
                    undefined,
                    quote ? { amount: quote.amount, currency: quote.currency } : null,
                    true
                  );
                }}>
                <Text style={styles.sheetActionText}>{t('call.videoNow')}</Text>
                {videoCallPrice && (
                  <Text style={styles.sheetActionNote} testID="call-video-price">
                    {Number(videoCallPrice.amount) === 0
                      ? t('call.priceFree')
                      : formatPerMinute(Number(videoCallPrice.amount), videoCallPrice.currency, targetLang, t('calendar.minutesShort'))}
                  </Text>
                )}
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.sheetAction}
              testID="call-schedule-action"
              onPress={() => {
                setCallActionMenu(false);
                openCallReservationModal();
              }}>
              <Text style={styles.sheetActionText}>{t('call.schedule')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.sheetCancel} onPress={() => setCallActionMenu(false)}>
              <Text style={styles.sheetCancelText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={callReservationModal}
        transparent
        animationType="fade"
        onRequestClose={() => setCallReservationModal(false)}>
        <KeyboardAvoidingView
          style={styles.keyboardFill}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <TouchableOpacity
            style={styles.sheetOverlay}
            accessible={false}
            activeOpacity={1}
            onPress={() => setCallReservationModal(false)}>
            <TouchableOpacity accessible={false} activeOpacity={1} onPress={() => {}}>
              <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
                <Text style={styles.priceModalTitle}>{t('call.scheduleTitle', { name: otherName })}</Text>
                {callPriceForReservation && (
                  <Text style={styles.meetupPriceNote} testID="call-schedule-price">
                    {t('call.priceNote', {
                      price: formatPerMinute(callPriceForReservation.amount, callPriceForReservation.currency, targetLang, t('calendar.minutesShort')),
                    })}
                  </Text>
                )}
                <TextInput
                  style={styles.priceModalInput}
                  value={callReservationSubject}
                  onChangeText={setCallReservationSubject}
                  placeholder={t('call.subjectPlaceholder')}
                  placeholderTextColor={colors.gray400}
                  maxLength={200}
                />
                <MeetupDateTimeField value={callReservationDate} onChange={setCallReservationDate} onValidityChange={setCallReservationDateValid} />
                <View style={styles.durationRow}>
                  {[15, 30, 60].map((mins) => (
                    <TouchableOpacity
                      key={mins}
                      style={[styles.durationChip, callReservationDurationText === String(mins) && styles.durationChipActive]}
                      onPress={() => setCallReservationDurationText(String(mins))}>
                      <Text style={[styles.durationChipText, callReservationDurationText === String(mins) && styles.durationChipTextActive]}>
                        {formatDisplayCount(mins, targetLang)} {t('calendar.minutesShort')}
                      </Text>
                    </TouchableOpacity>
                  ))}
                  <TextInput
                    style={styles.durationCustomInput}
                    value={callReservationDurationText}
                    onChangeText={(v) => setCallReservationDurationText(sanitizeDurationInput(v, targetLang))}
                    keyboardType="number-pad"
                    placeholder={t('calendar.minutesShort')}
                    placeholderTextColor={colors.gray400}
                  />
                </View>
                {callPriceForReservation && callReservationDuration !== null && (
                  <Text style={styles.meetupTotalCost}>
                    {t('calendar.totalCost', {
                      amount: formatMoney(callPriceForReservation.amount * callReservationDuration, callPriceForReservation.currency, targetLang),
                    })}
                  </Text>
                )}
                <TouchableOpacity
                  style={[styles.sheetAction, (callReservationSubmitting || callReservationDuration === null || !callReservationDateValid) && styles.disabledMeetupAction]}
                  disabled={callReservationSubmitting || callReservationDuration === null || !callReservationDateValid}
                  onPress={submitCallReservation}>
                  <Text style={styles.sheetActionText}>{t('call.sendReservation')}</Text>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </Modal>

      <Modal
        visible={attachMenu}
        transparent
        animationType="fade"
        onDismiss={launchPendingPhotoPicker}
        onRequestClose={() => setAttachMenu(false)}>
        <View style={styles.sheetOverlay}>
          <TouchableOpacity
            style={styles.sheetBackdrop}
            activeOpacity={1}
            accessible={false}
            onPress={() => setAttachMenu(false)}
          />
          <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
            <TouchableOpacity
              style={styles.sheetAction}
              onPress={() => openPhotoPickerAfterAttachMenu('camera')}>
              <Text style={styles.sheetActionText}>{t('message.takePhoto')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.sheetAction}
              onPress={() => openPhotoPickerAfterAttachMenu('library')}>
              <Text style={styles.sheetActionText}>{t('message.choosePhoto')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="attach-record-voice"
              style={styles.sheetAction}
              onPress={() => openPhotoPickerAfterAttachMenu('voice')}>
              <Text style={styles.sheetActionText}>{t('message.recordVoice')}</Text>
            </TouchableOpacity>
            {/* 9.1.9 — ısmarlama, kaydetmenin hemen altında: ikisi de ses ama
                birinde ödeyen sensin, diğerinde karşı taraf (D-559). */}
            <TouchableOpacity
              testID="attach-order-voice"
              style={styles.sheetAction}
              onPress={() => { setAttachMenu(false); setOrderMenu(true); }}>
              <Text style={styles.sheetActionText}>{t('message.voiceOrderAction')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="attach-send-location"
              style={styles.sheetAction}
              onPress={() => openPhotoPickerAfterAttachMenu('location')}>
              <Text style={styles.sheetActionText}>{t('message.sendLocation')}</Text>
            </TouchableOpacity>
            {!liveShare && (
              <TouchableOpacity
                testID="attach-share-live-location"
                style={styles.sheetAction}
                onPress={() => { setAttachMenu(false); setLiveShareMenu(true); }}>
                <Text style={styles.sheetActionText}>{t('message.shareLiveLocation')}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              testID="attach-choose-video"
              style={styles.sheetAction}
              onPress={() => openPhotoPickerAfterAttachMenu('video')}>
              <Text style={styles.sheetActionText}>{t('message.chooseVideo')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.sheetAction}
              onPress={() => { setAttachMenu(false); openMeetupModal(); }}>
              <Text style={styles.sheetActionText}>{t('calendar.proposeAction')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.sheetCancel} onPress={() => setAttachMenu(false)}>
              <Text style={styles.sheetCancelText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={liveShareMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setLiveShareMenu(false)}>
        <View style={styles.sheetOverlay}>
          <TouchableOpacity
            style={styles.sheetBackdrop}
            activeOpacity={1}
            accessible={false}
            onPress={() => setLiveShareMenu(false)}
          />
          <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
            <TouchableOpacity
              testID="live-location-1h"
              style={styles.sheetAction}
              onPress={() => startLiveLocation(60)}>
              <Text style={styles.sheetActionText}>{t('message.liveLocationHour')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="live-location-8h"
              style={styles.sheetAction}
              onPress={() => startLiveLocation(480)}>
              <Text style={styles.sheetActionText}>{t('message.liveLocationEightHours')}</Text>
            </TouchableOpacity>
            {/* Süresiz seçenek D-575'in kararı: kullanıcı elle durdurana kadar. */}
            <TouchableOpacity
              testID="live-location-open"
              style={styles.sheetAction}
              onPress={() => startLiveLocation(null)}>
              <Text style={styles.sheetActionText}>{t('message.liveLocationOpenEnded')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.sheetAction} onPress={() => setLiveShareMenu(false)}>
              <Text style={styles.sheetCancelText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <LiveLocationPanel
        visible={watchedShareId !== null}
        shareId={watchedShareId ?? 0}
        fetchPosition={(id) => api.liveLocationPosition(token, id)}
        onClose={() => setWatchedShareId(null)}
      />

      {/* Tek kayıt ekranı iki iş görüyor. Sipariş kipinde tavan siparişin
          süresi ve taksimetre yok; serbest kipte 60 dakika ve artan tutar. */}
      <VoiceRecorderSheet
        visible={voiceSheet || siparisTeslim !== null}
        pricePerMinute={otherPriceVoice?.price ?? 0}
        currency={otherPriceVoice?.currency ?? 'USD'}
        language={targetLang}
        orderSeconds={siparisTeslim?.seconds ?? null}
        onCancel={() => { setVoiceSheet(false); setSiparisTeslim(null); }}
        onComplete={siparisTeslim !== null ? deliverOrder : sendVoice}
      />

      {/* 9.1.9 — süre seçimi. Her satır KENDİ tutarını gösteriyor: onaydan
          sonra öğrenilen bir fiyat, onay sayılmaz (D-285'in aynı mantığı). */}
      <Modal
        visible={orderMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setOrderMenu(false)}>
        <View style={styles.sheetOverlay}>
          <TouchableOpacity
            style={styles.sheetBackdrop}
            activeOpacity={1}
            accessible={false}
            onPress={() => setOrderMenu(false)}
          />
          <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
            <Text style={styles.sheetTitle}>{t('message.voiceOrderPick')}</Text>
            {VOICE_ORDER_CHOICES.map((saniye) => (
              <TouchableOpacity
                key={saniye}
                testID={`voice-order-${saniye}`}
                disabled={orderBusy}
                style={styles.sheetAction}
                onPress={() => placeVoiceOrder(saniye)}>
                <Text style={styles.sheetActionText}>
                  {formatOrderLength(saniye)}
                  {otherPriceVoice
                    ? ` · ${formatMoney(voiceNoteCost(saniye, otherPriceVoice.price), otherPriceVoice.currency, targetLang)}`
                    : ''}
                </Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={styles.sheetAction} onPress={() => setOrderMenu(false)}>
              <Text style={styles.sheetCancelText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={meetupModal}
        transparent
        animationType="fade"
        onRequestClose={() => setMeetupModal(false)}>
        <KeyboardAvoidingView
          style={styles.keyboardFill}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <TouchableOpacity
            style={styles.sheetOverlay}
            activeOpacity={1}
            onPress={() => setMeetupModal(false)}>
            <TouchableOpacity activeOpacity={1} onPress={() => {}}>
              <View style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
                <Text style={styles.priceModalTitle}>{t('calendar.proposeTitle', { name: otherName })}</Text>
                {otherPriceTime && (
                  <Text style={styles.meetupPriceNote}>
                    {t('calendar.priceNote', {
                      price: formatPerMinute(Number(otherPriceTime.price), otherPriceTime.currency, targetLang, t('calendar.minutesShort')),
                    })}
                  </Text>
                )}
                <MeetupDateTimeField value={meetupDate} onChange={setMeetupDate} onValidityChange={setMeetupDateValid} />
                <View style={styles.durationRow}>
                  {[30, 60, 120].map((mins) => (
                    <TouchableOpacity
                      key={mins}
                      style={[styles.durationChip, meetupDurationText === String(mins) && styles.durationChipActive]}
                      onPress={() => setMeetupDurationText(String(mins))}>
                      <Text style={[styles.durationChipText, meetupDurationText === String(mins) && styles.durationChipTextActive]}>
                        {formatDisplayCount(mins, targetLang)} {t('calendar.minutesShort')}
                      </Text>
                    </TouchableOpacity>
                  ))}
                  <TextInput
                    style={styles.durationCustomInput}
                    value={meetupDurationText}
                    onChangeText={(v) => setMeetupDurationText(sanitizeDurationInput(v, targetLang))}
                    keyboardType="number-pad"
                    placeholder={t('calendar.minutesShort')}
                    placeholderTextColor={colors.gray400}
                  />
                </View>
                {otherPriceTime && meetupDuration !== null && (
                  <Text style={styles.meetupTotalCost}>
                    {t('calendar.totalCost', {
                      amount: formatMoney(Number(otherPriceTime.price) * meetupDuration, otherPriceTime.currency, targetLang),
                    })}
                  </Text>
                )}
                <TextInput
                  style={styles.priceModalInput}
                  value={meetupLocation}
                  onChangeText={setMeetupLocation}
                  placeholder={t('calendar.locationPlaceholder')}
                  placeholderTextColor={colors.gray400}
                />
                <TouchableOpacity
                  style={[styles.sheetAction, (meetupSubmitting || meetupDuration === null || !meetupDateValid) && styles.disabledMeetupAction]}
                  disabled={meetupSubmitting || meetupDuration === null || !meetupDateValid}
                  onPress={submitMeetup}>
                  <Text style={styles.sheetActionText}>{t('calendar.sendProposal')}</Text>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </Modal>

      {/* Paylaşım açıkken gösterge yazma çubuğunun HEMEN üstünde duruyor:
          süresiz paylaşımı kapatan bir saat olmadığı için (D-575) korumanın
          tamamı bunun görünür kalması. */}
      {liveShare && (
        <LiveLocationBar
          expiresAt={liveShare.expires_at}
          lastViewedAt={liveShare.last_viewed_at}
          onStop={stopLiveLocation}
        />
      )}
      {draftLink && linkPrefs && (linkPrefs.ask || linkView !== 'plain') && (
        // 9.32 / D-655 — varsayılan görünüm ayarlardan gelir; burada yalnız
        // DİĞER iki seçenek durur. Seçilen yine dokununca varsayılana döner.
        <View testID="link-view-chooser" style={styles.linkChooser}>
          {linkPrefs.ask && (
            <View style={styles.linkChooserRow} accessibilityRole="radiogroup" accessibilityLabel={t('message.linkViewLabel')}>
              {([
                ['compact', 'message.linkViewCompact'],
                ['plain', 'message.linkViewPlain'],
                ['card', 'message.linkViewCard'],
              ] as const).filter(([view]) => view !== linkPrefs.view).map(([view, label]) => (
                <TouchableOpacity
                  key={view}
                  testID={`link-view-${view}`}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: linkOverride === view }}
                  onPress={() => toggleLinkOverride(view)}
                  style={[styles.linkChip, linkOverride === view && styles.linkChipOn]}>
                  <Text style={[styles.linkChipText, linkOverride === view && styles.linkChipTextOn]}>{t(label)}</Text>
                </TouchableOpacity>
              ))}
              {linkPreviewLoading && <ActivityIndicator size="small" />}
            </View>
          )}
          {linkPreviewLoading && linkView !== 'plain' && (
            <Text style={styles.linkChooserNote}>{t('message.linkPreviewLoading')}</Text>
          )}
          {linkPreviewFailed && linkView !== 'plain' && (
            <Text testID="link-preview-failed" style={styles.linkChooserNote}>{t('message.linkPreviewFailed')}</Text>
          )}
          {!linkPreviewLoading && linkView !== 'plain' && linkPreview?.url === draftLink && (
            <View testID="link-preview-draft" style={styles.linkDraft}>
              {linkView === 'card' && linkPreview.image ? (
                <Image source={{ uri: linkPreview.image }} style={styles.linkDraftImage} />
              ) : null}
              {/* Hap kalan genişliğe sığar; yer daralınca başlık kısalır, adres
                  kısalmaz (simülatörde görsel yanındayken ".com" kesiliyordu). */}
              <View style={styles.linkDraftPill}>
                <LinkPill preview={{ ...linkPreview, view: linkView }} mine={false} onPress={() => {}} />
              </View>
            </View>
          )}
        </View>
      )}
      <View style={[styles.inputBar, { paddingBottom: insets.bottom + spacing.sm }]}>
        <TouchableOpacity
          onPress={() => setAttachMenu(true)}
          disabled={sending}
          style={styles.photoBtn}
          accessibilityRole="button"
          accessibilityLabel={t('message.addAttachment')}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
          <Text style={styles.photoBtnText}>+</Text>
        </TouchableOpacity>
        <TextInput
          style={styles.input}
          placeholder={t('message.inputPlaceholder')}
          placeholderTextColor={colors.gray400}
          value={text}
          onChangeText={(val) => {
            // Native'de (iOS/Android) dokunmatik klavyenin "return" tuşu her zaman
            // önce metne "\n" ekler, SONRA onChangeText'i çağırır — onKeyPress'in
            // web'deki gibi preventDefault ile bunu engelleme imkanı yok. Bu yüzden
            // burada yakalıyoruz: imleç sonda iken tek bir "\n" eklendiyse (= return
            // tuşuna basıldı), satır sonu eklemek yerine mesajı gönder.
            if (
              Platform.OS !== 'web' &&
              val.length === text.length + 1 &&
              val.startsWith(text) &&
              val.endsWith('\n')
            ) {
              send();
              return;
            }
            setText(val);
            if (val.length > 0 && !typingThrottle.current) {
              api.sendTyping(token).catch(() => {});
              typingThrottle.current = setTimeout(() => {
                typingThrottle.current = null;
              }, 2000) as unknown as number;
            }
          }}
          onKeyPress={handleKeyPress}
          returnKeyType="send"
          multiline
        />
        <TouchableOpacity
          style={[styles.sendButton, (!text.trim() || sending) && styles.sendDisabled]}
          disabled={!text.trim() || sending}
          onPress={send}
          accessibilityRole="button"
          accessibilityLabel={t('message.sendMessageAction')}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}>
          <Text style={styles.sendText}>→</Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const makeStyles = (colors: ThemeColors, _isDark: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  keyboardFill: { flex: 1 },
  header: {
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: colors.gray100,
  },
  disabledMeetupAction: { opacity: 0.5 },
  priceModalTitle: { fontSize: fontSize.md, color: colors.black, textAlign: 'center', paddingHorizontal: spacing.lg, marginBottom: spacing.md },
  meetupPriceNote: { fontSize: fontSize.sm, color: colors.gray600, textAlign: 'center', paddingHorizontal: spacing.lg, marginTop: -spacing.sm, marginBottom: spacing.md },
  durationRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  durationChip: {
    flexGrow: 1,
    flexBasis: 0,
    flexShrink: 1,
    ...formControlMetrics,
    paddingVertical: 0,
    paddingHorizontal: 0,
    borderColor: colors.gray200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  durationChipActive: { backgroundColor: colors.black, borderColor: colors.black },
  durationChipText: { fontSize: fontSize.md, fontWeight: '600', color: colors.gray600 },
  durationChipTextActive: { color: colors.white },
  durationCustomInput: {
    flexGrow: 1,
    flexBasis: 0,
    flexShrink: 1,
    ...formControlMetrics,
    paddingVertical: 0,
    paddingHorizontal: 0,
    borderColor: colors.gray200,
    textAlign: 'center',
    fontSize: fontSize.md,
    fontWeight: '600',
    color: colors.black,
  },
  meetupTotalCost: { fontSize: fontSize.md, fontWeight: '700', color: colors.black, textAlign: 'center', marginBottom: spacing.md },
  priceModalInput: {
    ...formControlMetrics,
    borderColor: colors.gray200,
    paddingHorizontal: spacing.md,
    paddingVertical: 0,
    fontSize: fontSize.md,
    color: colors.black,
    textAlign: 'center',
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  headerCenter: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginHorizontal: spacing.sm },
  headerIdentity: { flex: 1, minWidth: 0 },
  headerAvatar: { width: 28, height: 28, borderRadius: 14 },
  // 9.26 / D-641 — uzun adda kısalan ad olsun, rozet değil.
  headerNameRow: { flexDirection: 'row', alignItems: 'center', minWidth: 0 },
  headerName: { fontSize: fontSize.md, fontWeight: '600', color: colors.black, flexShrink: 1 },
  headerLastSeen: { fontSize: 11, color: colors.gray400, marginTop: 1 },
  // Fiyat notunun kartıyla aynı yüzey: sohbetin başındaki sistem notları tek dilde.
  encryptionNotice: {
    alignSelf: 'center',
    maxWidth: '86%',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.card,
    backgroundColor: colors.gray100,
  },
  unencryptedText: { fontSize: 12, fontWeight: '600', color: colors.black },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  list: { padding: spacing.lg, gap: spacing.sm },
  rowMine: { alignItems: 'flex-end' },
  seenAt: { fontSize: 10, color: colors.gray400, marginTop: 2, marginRight: 2 },
  messageLink: { textDecorationLine: 'underline' },
  // 9.32 / D-653 — yazma alanının üstündeki link görünümü seçimi.
  linkChooser: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    gap: 6,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.gray200,
    backgroundColor: colors.white,
  },
  linkChooserRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  linkChip: {
    minHeight: 32,
    justifyContent: 'center',
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.gray200,
  },
  linkChipOn: { backgroundColor: colors.black, borderColor: colors.black },
  linkChipText: { fontSize: fontSize.sm, color: colors.gray600 },
  linkChipTextOn: { color: colors.white },
  linkChooserNote: { fontSize: 12, color: colors.gray600 },
  linkDraft: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  linkDraftImage: { width: 48, height: 25, borderRadius: 4 },
  linkDraftPill: { flex: 1, minWidth: 0 },
  rowOther: { alignItems: 'flex-start' },
  bubble: {
    maxWidth: '75%',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.card,
  },
  bubbleMine: {
    backgroundColor: colors.black,
    borderBottomRightRadius: 4,
  },
  bubbleOther: {
    backgroundColor: colors.gray100,
    borderBottomLeftRadius: 4,
  },
  imageBubble: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
  },
  callBubble: {
    minWidth: 190,
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    borderRadius: radius.card,
    borderWidth: 1,
  },
  callBubbleMine: {
    backgroundColor: colors.black,
    borderColor: colors.black,
    borderBottomRightRadius: 4,
  },
  callBubbleOther: {
    backgroundColor: colors.gray100,
    borderColor: colors.gray200,
    borderBottomLeftRadius: 4,
  },
  callCard: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  callIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.gray200,
  },
  callIconMine: { backgroundColor: 'rgba(255,255,255,0.14)' },
  callIconMissed: { backgroundColor: 'rgba(217,74,69,0.10)' },
  callCardCopy: { minWidth: 0, flexShrink: 1 },
  callCardTitle: { flexShrink: 1, fontSize: fontSize.md, fontWeight: '600', color: colors.black },
  callCardTitleMine: { color: colors.white },
  callCardTitleMissed: { color: '#D94A45' },
  // Yön oku (↗ giden / ↙ gelen): arrow-up/down 45° döndürülür. Kart geçmiş
  // listesiyle aynı dil; renk başlıkla aynı (mine→beyaz, cevapsız→kırmızı).
  callTitleRow: { flexDirection: 'row', alignItems: 'center', flexShrink: 1 },
  callDirArrow: { transform: [{ rotate: '45deg' }], marginRight: 3 },
  callCardDetail: { marginTop: 2, fontSize: fontSize.sm, color: colors.gray600 },
  callCardDetailMine: { color: 'rgba(255,255,255,0.72)' },
  photoLocation: { paddingTop: 6, maxWidth: MSG_IMG_WIDTH },
  photoLocationPlace: { fontSize: fontSize.sm, color: colors.gray600 },
  photoLocationSource: { fontSize: 10, color: colors.gray400, marginTop: 1 },
  bubbleCancelled: {
    backgroundColor: colors.gray100,
  },
  bubbleText: { fontSize: fontSize.md, color: colors.black },
  emojiBubble: {
    backgroundColor: 'transparent', borderWidth: 0, borderRadius: 0,
    borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
    minWidth: 48, minHeight: 48, paddingHorizontal: 4, paddingVertical: spacing.sm,
  },
  emojiMessageText: { fontSize: 32, lineHeight: 44 },
  emojiMessageTime: { color: colors.gray600 },
  emojiHighlight: { opacity: 0.6 },
  bubbleTextMine: { color: colors.white },
  meetupCard: { gap: 2 },
  meetupCardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  meetupCardTitle: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
  meetupCardWhen: { fontSize: fontSize.sm, color: colors.black },
  quoteBox: {
    borderLeftWidth: 3,
    borderLeftColor: colors.gray400,
    paddingLeft: spacing.sm,
    marginBottom: spacing.xs,
    opacity: 0.9,
  },
  quoteBoxMine: { borderLeftColor: 'rgba(255,255,255,0.6)' },
  // 9.22.2 — alıntıdan atlanan kaynak baloncuk kısa süre çerçeveyle vurgulanır.
  bubbleHighlight: { borderWidth: 2, borderColor: colors.black },
  quoteAuthor: { fontSize: 11, fontWeight: '700', color: colors.gray600 },
  quotePreview: { fontSize: 12, color: colors.gray600 },
  quoteTextMine: { color: 'rgba(255,255,255,0.85)' },
  msgTime: { fontSize: 10, color: colors.gray400, marginTop: 4, textAlign: 'right' },
  msgTimeMine: { color: 'rgba(255,255,255,0.6)' },
  // Okundu göstergesi: çift tık okununca mavi (görüldü), değilse zaman rengini alır.
  tickRead: { color: '#34B7F1', fontWeight: '700' },
  dateSep: {
    fontSize: 11,
    color: colors.gray400,
    textAlign: 'center',
    marginVertical: spacing.md,
  },
  // 9.34 / D-634 — kimlik değişikliği satırı. Gün ayracıyla aynı sessiz dilde:
  // bu bir alarm değil, bir bilgi. Kırmızı bir uyarı, sebebi çoğu zaman yeni
  // bir telefon olan sıradan bir olayı korkutucu gösterirdi.
  identityChangeLine: {
    fontSize: 12,
    color: colors.gray400,
    textAlign: 'center',
    marginVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    lineHeight: 17,
  },
  typingRow: { paddingHorizontal: spacing.lg, paddingBottom: spacing.xs },
  typingBubble: {
    backgroundColor: colors.gray100,
    borderRadius: radius.card,
    borderBottomLeftRadius: 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignSelf: 'flex-start',
  },
  typingText: { fontSize: fontSize.lg, color: colors.gray400, letterSpacing: 2 },
  cancelBtn: { marginTop: 4 },
  cancelText: { fontSize: 11, color: colors.gray400 },
  replyBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.gray100,
    gap: spacing.sm,
  },
  replyBarLine: { width: 3, alignSelf: 'stretch', backgroundColor: colors.black, borderRadius: 2 },
  // Sohbet başı ücret özeti — sessiz bir kart, uyarı değil bilgi.
  conversationPriceNotice: {
    alignSelf: 'center',
    maxWidth: '86%',
    marginBottom: spacing.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.card,
    backgroundColor: colors.gray100,
  },
  priceNoticeHeading: { fontSize: 12, fontWeight: '600', color: colors.black, marginBottom: 4 },
  priceNoticeLine: { fontSize: 12, color: colors.gray400, marginTop: 1 },
  priceNoticePersonal: { fontSize: 11, color: colors.gray400, marginTop: 6, fontStyle: 'italic' },
  sheetActionNote: { fontSize: 11, color: colors.gray400, marginTop: 2 },
  replyBarContent: { flex: 1 },
  replyBarAuthor: { fontSize: 12, fontWeight: '700', color: colors.black },
  replyBarPreview: { fontSize: 12, color: colors.gray400 },
  replyBarClose: { fontSize: fontSize.md, color: colors.gray400, paddingHorizontal: 4 },
  // 9.21.9 / D-642 — tek hap, balonun alt kenarına biniyor.
  reactionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginTop: -8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: colors.white,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.gray200,
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  // Balonun köşesinden biraz içeride; karşınınkinde solda, benimkinde sağda.
  reactionPillOther: { alignSelf: 'flex-start', marginLeft: spacing.md },
  reactionPillMine: { alignSelf: 'flex-end', marginRight: spacing.md },
  // Her emoji kendi sütunu: üstte emoji, altında küçük sayı. Dokunma hedefi
  // sütunun tamamı.
  reactionItem: { alignItems: 'center', paddingHorizontal: 3, paddingTop: 1, borderRadius: 10 },
  // Benim verdiğim tepki hafif bir zeminle ayrılıyor; çerçeve hapın içinde kalabalık ederdi.
  reactionItemMine: { backgroundColor: colors.gray100 },
  reactionEmoji: { fontSize: 17, lineHeight: 21 },
  reactionCount: { fontSize: 8, lineHeight: 9, color: colors.gray600, fontWeight: '600', marginTop: -1 },
  reactionCountMine: { color: colors.black },
  sheetOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.35)', justifyContent: 'flex-end' },
  sheetBackdrop: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 },
  sheet: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  emojiRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
  },
  // Tepki düğmesi yuvarlak bir hap; ölçüsü yoktu, yani yarıçap kuralı da
  // uygulanamıyordu. 44×44 vermek hem kuralı ifade edilebilir kılıyor hem de
  // düğmeyi dokunma tabanının üstüne çıkarıyor — emoji + dolgu 42 pt kalıyordu.
  emojiBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  emojiBtnActive: { backgroundColor: colors.gray100 },
  emojiText: { fontSize: 26 },
  sheetAction: {
    paddingVertical: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.gray100,
  },
  sheetTitle: {
    fontSize: fontSize.sm,
    color: colors.gray600,
    textAlign: 'center',
    paddingBottom: spacing.xs,
  },
  sheetActionText: {
    fontSize: fontSize.md,
    color: colors.black,
    fontWeight: '600',
    textAlign: 'center',
  },
  sheetCancel: { paddingVertical: spacing.md, alignItems: 'center', marginTop: spacing.xs },
  sheetCancelText: { color: colors.gray600, fontSize: fontSize.md },
  inputBar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.gray100,
    gap: spacing.sm,
  },
  input: {
    flex: 1,
    fontSize: fontSize.md,
    color: colors.black,
    maxHeight: 100,
    paddingVertical: spacing.sm,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.black,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendDisabled: { backgroundColor: colors.gray200 },
  sendText: { color: colors.white, fontSize: fontSize.lg },
  photoBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.gray100,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 2,
  },
  photoBtnText: { fontSize: 20, lineHeight: 22, color: colors.gray600, fontWeight: '400' },
});
